import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.tsx';
import { postBacktest, getBacktestProgress } from '../../lib/api/backtests';
import { createShareLink } from '../../lib/api/shareLinks';
import { fetchBacktestConfigs } from '../../lib/api/backtestConfigs';
import { supabase } from '../../supabase';
import { useAuth } from '../../AuthContext';
import { validateBacktestDateRange, getMaxBacktestYears, getTierDisplayName, getEarliestScreenDate } from '../../utils/tiers';
import { useScreenBuilder } from '../../ScreenBuilderContext';
import { normalizeBacktestResult, sortinoFromDaily } from '../../lib/backtests/normalizeBacktestResult';
import { Plus, X, Share2 } from 'lucide-react';
import '../strategy/LinearStrategyEditor.css';

import Stepper from '../strategy/Stepper.tsx';
import FormulaLibrary from '../strategy/FormulaLibrary.js';
import FormulaEditor from '../strategy/FormulaEditor.tsx';
import SelectionEditorV2 from '../strategy/SelectionEditorV2.tsx';
import EnhancedResultsPanel from '../strategy/EnhancedResultsPanel.tsx';
import OptimizationResultsPanel from '../strategy/OptimizationResultsPanel.tsx';
import { DEFAULT_SELECTION_V2 } from '../../types/selectionV2.ts';
import PremiumSignupBanner from '../PremiumSignupBanner.jsx';

const SP500_PRIMARY_CODE = 'sp500_gh';
const SP500_ALIASES = ['sp500', 'sp500_fmp', SP500_PRIMARY_CODE];
const normalizeUniverseType = (value) => {
  if (!value) return value;
  return SP500_ALIASES.includes(value) ? SP500_PRIMARY_CODE : value;
};

// Consolidate S&P 500 choices (GitHub/FMP/legacy) into one UI option that always maps to the GitHub-based dataset; legacy codes are still normalized to keep old screens/backtests loading.
const INDEX_UNIVERSE_OPTIONS = [
  { value: SP500_PRIMARY_CODE, label: 'S&P 500' },
  { value: 'nasdaq100', label: 'Nasdaq-100' },
  { value: 'dow30', label: 'Dow Jones 30' }
];

const INDEX_UNIVERSE_TYPES = INDEX_UNIVERSE_OPTIONS.map(opt => opt.value);
const INDEX_NAME_MAP = INDEX_UNIVERSE_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {
  sp500: 'S&P 500',
  sp500_fmp: 'S&P 500'
});

const DEFAULT_ASSET_CLASS = 'stocks';
const ASSET_CLASS_OPTIONS = [
  { value: 'stocks', label: 'Stocks' },
  { value: 'etfs', label: 'ETFs' },
  { value: 'crypto', label: 'Crypto' },
  { value: 'commodities', label: 'Commodities' }
];
const ASSET_CLASS_LABELS = {
  stocks: { label: 'Stocks', singular: 'Stock' },
  etfs: { label: 'ETFs', singular: 'ETF' },
  crypto: { label: 'Crypto', singular: 'Crypto' },
  commodities: { label: 'Commodities', singular: 'Commodity' }
};

const normalizeAssetClass = (value) => {
  const normalized = String(value || '').toLowerCase().trim();
  if (['stock', 'stocks', 'equity', 'equities'].includes(normalized)) return 'stocks';
  if (['etf', 'etfs'].includes(normalized)) return 'etfs';
  if (['crypto', 'cryptos', 'cryptoasset', 'cryptoassets'].includes(normalized)) return 'crypto';
  if (['commodity', 'commodities'].includes(normalized)) return 'commodities';
  return DEFAULT_ASSET_CLASS;
};

const getAssetClassLabel = (assetClass) => {
  const key = normalizeAssetClass(assetClass);
  return ASSET_CLASS_LABELS[key]?.label || 'Assets';
};

const getAssetClassSingular = (assetClass) => {
  const key = normalizeAssetClass(assetClass);
  return ASSET_CLASS_LABELS[key]?.singular || 'Asset';
};

const cloneSelectionConfig = (config) => JSON.parse(JSON.stringify(config || DEFAULT_SELECTION_V2));

const normalizeSymbolList = (symbols = []) => (
  Array.isArray(symbols)
    ? symbols
        .map((sym) => {
          if (typeof sym === 'string') return sym;
          if (sym && typeof sym === 'object') {
            return sym.ticker || sym.symbol || '';
          }
          return '';
        })
        .map(sym => String(sym || '').trim().toUpperCase())
        .filter(Boolean)
    : []
);

const parseSymbolTokens = (input) => (
  String(input || '')
    .split(/[\s,;]+/)
    .map((token) => token.trim().toUpperCase())
    .filter(Boolean)
);

const DEFAULT_WEIGHT_TOTAL = 100;
const roundWeight = (value, decimals = 2) => {
  const factor = 10 ** decimals;
  return Math.round((value + Number.EPSILON) * factor) / factor;
};
const sanitizeWeight = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return Math.max(0, num);
};
const WEIGHT_FORMULA_PRESETS = [
  { label: '100%', value: '100' },
  { label: '50%', value: '50' },
  { label: '33.33%', value: '33.33' },
  { label: '25%', value: '25' },
  {
    label: 'Sell in May and go away, but remember to come back in September',
    value: 'if(MONTH >= 5 && MONTH < 9, 0, 100)'
  },
  { label: 'Halving < 365d', value: 'if(days_since(BITCOIN_HALVING) < 365, 70, 30)' },
  { label: 'IF BTC Mom > 0', value: 'if(context(CRYPTO:BTCUSD, momentum(252)) > 0, 70, 30)' },
  { label: 'IF SPY > SMA200', value: 'if(context(STOCKS:SPY, price) > context(STOCKS:SPY, sma(200)), 70, 30)' }
];
const WEIGHT_FORMULA_SIMPLE_RE = /^[0-9+\-*/().\s]+$/;
const WEIGHT_FORMULA_NUMBER_RE = /^\d+(\.\d+)?$/;
const normalizeWeightFormulaInput = (value) => {
  const raw = String(value ?? '');
  if (!/[A-Za-z]/.test(raw)) {
    return raw.replace(/,/g, '.');
  }
  return raw;
};
const evaluateWeightFormula = (formula) => {
  if (typeof formula !== 'string') return null;
  const normalized = normalizeWeightFormulaInput(formula).trim();
  if (!normalized) return null;
  if (!WEIGHT_FORMULA_SIMPLE_RE.test(normalized)) return null;
  try {
    // eslint-disable-next-line no-new-func
    const value = Function(`"use strict"; return (${normalized});`)();
    return Number.isFinite(value) ? value : null;
  } catch (err) {
    return null;
  }
};
const formatWeightFormula = (value) => (
  Number.isFinite(value) ? String(roundWeight(value)) : ''
);
const formatDebugNumber = (value) => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return 'N/A';
  }
  const abs = Math.abs(value);
  if (abs > 1000) return value.toFixed(2);
  if (abs > 0 && abs < 0.01) return value.toFixed(6);
  return value.toFixed(4);
};
const OPTIMIZER_MAX_STEPS = 20;
const OPTIMIZER_IDENT_RE = /[A-Za-z_][A-Za-z0-9_]*/g;
const isOptimizerIdent = (ident) => {
  if (!ident) return false;
  const trimmed = ident.replace(/^_+|_+$/g, '').toLowerCase();
  return trimmed === 'x';
};
const collectOptimizerVarsFromExpr = (expr, out) => {
  if (typeof expr !== 'string' || !expr.trim()) return;
  const matches = expr.match(OPTIMIZER_IDENT_RE) || [];
  matches.forEach((ident) => {
    if (isOptimizerIdent(ident)) {
      out.add(ident.toLowerCase());
    }
  });
};
const collectOptimizerVarsFromSelection = (selection, out) => {
  if (!selection || typeof selection !== 'object') return;
  collectOptimizerVarsFromExpr(selection.factor, out);
  if (selection.rankPick && typeof selection.rankPick === 'object') {
    collectOptimizerVarsFromExpr(selection.rankPick.formula, out);
    if (Array.isArray(selection.rankPick.ties)) {
      selection.rankPick.ties.forEach((tie) => {
        if (tie && typeof tie === 'object') {
          collectOptimizerVarsFromExpr(tie.field, out);
        }
      });
    }
  }
  if (selection.filters && typeof selection.filters === 'object') {
    const groups = Array.isArray(selection.filters.groups) ? selection.filters.groups : [];
    groups.forEach((group) => {
      if (!group || typeof group !== 'object') return;
      const rules = Array.isArray(group.rules) ? group.rules : [];
      rules.forEach((rule) => {
        if (rule && typeof rule === 'object') {
          collectOptimizerVarsFromExpr(rule.left, out);
        }
      });
    });
  }
};
const collectOptimizerVarsFromElements = (elements) => {
  const vars = new Set();
  (elements || []).forEach((element) => {
    if (!element || typeof element !== 'object') return;
    collectOptimizerVarsFromExpr(element.weight_formula || element.weightFormula, vars);
    if (Array.isArray(element.formulas)) {
      element.formulas.forEach((formula) => {
        if (formula && typeof formula === 'object') {
          collectOptimizerVarsFromExpr(formula.formula, vars);
        }
      });
    }
    if (element.selection) {
      collectOptimizerVarsFromSelection(element.selection, vars);
    }
  });
  return vars;
};
const OPTIMIZER_TARGET_OPTIONS = [
  { value: 'total_return', label: 'Total Return' },
  { value: 'annualized_return', label: 'Annualized Return' },
  { value: 'volatility', label: 'Volatility' },
  { value: 'sharpe_ratio', label: 'Sharpe Ratio' },
  { value: 'sortino_ratio', label: 'Sortino Ratio' },
  { value: 'max_drawdown', label: 'Max Drawdown' },
  { value: 'win_rate', label: 'Win Rate' },
  { value: 'avg_trade_return', label: 'Avg Trade Return' },
  { value: 'information_ratio', label: 'Information Ratio' },
  { value: 'time_under_water', label: 'Time Under Water' },
  { value: 'avg_holding_days', label: 'Avg Holding Days' }
];
const getElementWeightValue = (element) => {
  const formula = element?.weight_formula;
  if (typeof formula === 'string' && formula.trim()) {
    const value = evaluateWeightFormula(formula);
    return value === null ? null : value;
  }
  if (typeof element?.weight === 'number' && Number.isFinite(element.weight)) {
    return element.weight;
  }
  return 0;
};
const getElementWeightNumber = (element) => {
  const value = getElementWeightValue(element);
  if (!Number.isFinite(value)) return 0;
  return sanitizeWeight(value);
};
const normalizePortfolioWeights = (elements, { mode = 'normalize', total = DEFAULT_WEIGHT_TOTAL } = {}) => {
  if (!Array.isArray(elements) || elements.length === 0) return [];
  const cleanWeights = elements.map((el) => getElementWeightNumber(el));
  const sum = cleanWeights.reduce((acc, val) => acc + val, 0);
  if (mode === 'equal' || sum <= 0) {
    const equalWeight = total / elements.length;
    return elements.map((el) => ({
      ...el,
      weight: roundWeight(equalWeight),
      weight_formula: formatWeightFormula(equalWeight)
    }));
  }
  const scale = total / sum;
  return elements.map((el, idx) => {
    const weight = roundWeight(cleanWeights[idx] * scale);
    return {
      ...el,
      weight,
      weight_formula: formatWeightFormula(weight)
    };
  });
};
const normalizeWeightsForState = (elements) => {
  if (!Array.isArray(elements) || elements.length === 0) return [];
  const hasNonNumericFormula = elements.some((el) => {
    const formulaRaw = typeof el.weight_formula === 'string' ? el.weight_formula.trim() : '';
    return formulaRaw && evaluateWeightFormula(formulaRaw) === null;
  });
  if (hasNonNumericFormula) {
    return elements.map((el) => {
      const formulaRaw = typeof el.weight_formula === 'string' ? el.weight_formula.trim() : '';
      const normalizedFormula = formulaRaw ? normalizeWeightFormulaInput(formulaRaw) : '';
      const weightValue = evaluateWeightFormula(normalizedFormula);
      const fallbackWeight = typeof el.weight === 'number' ? el.weight : DEFAULT_WEIGHT_TOTAL;
      const weight = Number.isFinite(weightValue) ? weightValue : fallbackWeight;
      return {
        ...el,
        weight,
        weight_formula: normalizedFormula || formatWeightFormula(weight)
      };
    });
  }
  const cleanWeights = elements.map((el) => getElementWeightNumber(el));
  const sum = cleanWeights.reduce((acc, val) => acc + val, 0);
  if (sum <= 0) {
    return normalizePortfolioWeights(elements, { mode: 'equal' });
  }
  if (sum <= 1.01) {
    const scale = DEFAULT_WEIGHT_TOTAL / sum;
    return elements.map((el, idx) => {
      const weight = roundWeight(cleanWeights[idx] * scale);
      const formulaRaw = typeof el.weight_formula === 'string' ? el.weight_formula.trim() : '';
      const shouldUpdateFormula = !formulaRaw || WEIGHT_FORMULA_NUMBER_RE.test(formulaRaw);
      return {
        ...el,
        weight,
        weight_formula: shouldUpdateFormula ? formatWeightFormula(weight) : el.weight_formula
      };
    });
  }
  return elements.map((el, idx) => {
    const weight = roundWeight(cleanWeights[idx]);
    const formulaRaw = typeof el.weight_formula === 'string' ? el.weight_formula.trim() : '';
    const shouldUpdateFormula = !formulaRaw || WEIGHT_FORMULA_NUMBER_RE.test(formulaRaw);
    return {
      ...el,
      weight,
      weight_formula: shouldUpdateFormula ? formatWeightFormula(weight) : el.weight_formula
    };
  });
};

const createUniverseDefaults = (assetClass) => {
  const normalizedAssetClass = normalizeAssetClass(assetClass);
  const isStocks = normalizedAssetClass === 'stocks';
  const universeType = isStocks ? SP500_PRIMARY_CODE : 'custom';
  const universeName = isStocks
    ? (INDEX_NAME_MAP[SP500_PRIMARY_CODE] || 'S&P 500')
    : `Custom ${getAssetClassLabel(normalizedAssetClass)}`;
  return {
    asset_class: normalizedAssetClass,
    type: universeType,
    watchlist_id: null,
    symbols: [],
    name: universeName,
    point_in_time: true,
    fundamental_lag_days: 0
  };
};

let portfolioElementCounter = 1;
const generatePortfolioElementId = () => `element-${portfolioElementCounter++}`;

const resolveFormulasFromConfig = (config = {}) => {
  if (Array.isArray(config.formulas) && config.formulas.length > 0) {
    return config.formulas;
  }
  if (config.formula) {
    return [{ id: '1', name: 'Formula 1', formula: config.formula }];
  }
  return [];
};

const resolveSelectionConfigFromConfig = (config = {}, formulasToLoad = []) => {
  const selection = config.selection || null;
  if (selection && selection.rankPick) {
    let resolvedSelection = selection;
    if (selection.rankPick.formula && selection.rankPick.formula.startsWith('formula:')) {
      const formulaId = selection.rankPick.formula.split(':')[1];
      const matchingFormula = (formulasToLoad || []).find(f => f.id === formulaId);
      if (matchingFormula?.formula) {
        resolvedSelection = {
          ...selection,
          rankPick: {
            ...selection.rankPick,
            formula: matchingFormula.formula
          }
        };
      }
    }
    return {
      ...cloneSelectionConfig(DEFAULT_SELECTION_V2),
      ...resolvedSelection,
      filters: {
        groups: resolvedSelection.filters?.groups || []
      },
      rankPick: {
        ...DEFAULT_SELECTION_V2.rankPick,
        ...(resolvedSelection.rankPick || {})
      }
    };
  }
  if (config.selectionConditions && config.selectionConditions.length > 0) {
    return {
      ...cloneSelectionConfig(DEFAULT_SELECTION_V2),
      rankPick: {
        ...DEFAULT_SELECTION_V2.rankPick,
        formula: config.selectionConditions[0]?.relative?.factor || ''
      }
    };
  }
  return cloneSelectionConfig(DEFAULT_SELECTION_V2);
};

const resolveUniverseConfigFromConfig = (rawUniverse = {}, assetClassOverride) => {
  const assetClass = normalizeAssetClass(assetClassOverride || rawUniverse.asset_class);
  const defaults = createUniverseDefaults(assetClass);
  const rawType = rawUniverse.type || defaults.type;
  const normalizedType = normalizeUniverseType(rawType);
  const allowIndex = assetClass === 'stocks';
  const type = allowIndex && INDEX_UNIVERSE_TYPES.includes(normalizedType)
    ? normalizedType
    : (rawUniverse.type === 'watchlist' ? 'watchlist' : defaults.type);
  const name = rawUniverse.name
    || (INDEX_NAME_MAP[type] || defaults.name || 'Custom Watchlist');
  const symbols = normalizeSymbolList(
    Array.isArray(rawUniverse.symbols) ? rawUniverse.symbols : (defaults.symbols || [])
  );
  return {
    ...defaults,
    ...rawUniverse,
    asset_class: assetClass,
    type,
    watchlist_id: rawUniverse.watchlist_id || null,
    symbols,
    name,
    point_in_time: rawUniverse.point_in_time ?? defaults.point_in_time,
    fundamental_lag_days: rawUniverse.fundamental_lag_days ?? defaults.fundamental_lag_days
  };
};

const createPortfolioElement = (overrides = {}) => {
  const assetClass = normalizeAssetClass(overrides.asset_class || overrides.universe?.asset_class);
  const universe = resolveUniverseConfigFromConfig(overrides.universe || {}, assetClass);
  const weightFormulaRaw = overrides.weight_formula || overrides.weightFormula || '';
  const weightFormula = typeof weightFormulaRaw === 'string' ? weightFormulaRaw : '';
  const weightValue = evaluateWeightFormula(weightFormula);
  const weightFallback = typeof overrides.weight === 'number' ? overrides.weight : DEFAULT_WEIGHT_TOTAL;
  return {
    id: overrides.id || generatePortfolioElementId(),
    name: overrides.name || 'Element',
    weight: Number.isFinite(weightValue) ? weightValue : weightFallback,
    weight_formula: weightFormula ? normalizeWeightFormulaInput(weightFormula) : formatWeightFormula(weightFallback),
    asset_class: assetClass,
    universe,
    formulas: Array.isArray(overrides.formulas) ? overrides.formulas : [],
    selection: overrides.selection ? cloneSelectionConfig(overrides.selection) : cloneSelectionConfig(DEFAULT_SELECTION_V2)
  };
};

const resolvePortfolioElements = (config = {}) => {
  const portfolio = config.portfolio || {};
  if (Array.isArray(portfolio.elements) && portfolio.elements.length > 0) {
    const elements = portfolio.elements.map((element, index) => {
      const universe = resolveUniverseConfigFromConfig(element.universe || {}, element.asset_class);
      const formulas = resolveFormulasFromConfig(element);
      const selection = resolveSelectionConfigFromConfig(element, formulas);
      const weightFormulaRaw = element.weight_formula || element.weightFormula || '';
      const weightFormula = typeof weightFormulaRaw === 'string' ? weightFormulaRaw : '';
      const weightValue = evaluateWeightFormula(weightFormula);
      const weightFallback = typeof element.weight === 'number' ? element.weight : DEFAULT_WEIGHT_TOTAL;
      return {
        id: element.id || generatePortfolioElementId(),
        name: element.name || `Element ${index + 1}`,
        weight: Number.isFinite(weightValue) ? weightValue : weightFallback,
        weight_formula: weightFormula ? normalizeWeightFormulaInput(weightFormula) : formatWeightFormula(weightFallback),
        asset_class: normalizeAssetClass(element.asset_class || universe.asset_class),
        universe,
        formulas,
        selection
      };
    });
    return normalizeWeightsForState(elements);
  }

  const formulas = resolveFormulasFromConfig(config);
  const universe = resolveUniverseConfigFromConfig(config.universe || {}, config.universe?.asset_class);
  const selection = resolveSelectionConfigFromConfig(config, formulas);
  return [{
    id: generatePortfolioElementId(),
    name: 'Element 1',
    weight: DEFAULT_WEIGHT_TOTAL,
    weight_formula: formatWeightFormula(DEFAULT_WEIGHT_TOTAL),
    asset_class: normalizeAssetClass(universe.asset_class),
    universe,
    formulas,
    selection
  }];
};

const syncPortfolioCounter = (elements = []) => {
  let maxId = 0;
  elements.forEach((el) => {
    const match = String(el.id || '').match(/element-(\d+)/);
    if (match) {
      const num = Number(match[1]);
      if (!Number.isNaN(num)) {
        maxId = Math.max(maxId, num);
      }
    }
  });
  if (maxId >= portfolioElementCounter) {
    portfolioElementCounter = maxId + 1;
  }
};

const generateFormulaId = (existing = []) => {
  const used = new Set(existing.map(f => String(f.id ?? '')).filter(Boolean));
  let i = 0;
  while (used.has(String(i))) i += 1;
  return String(i);
};

const BacktestProgressOverlay = ({ progress }) => {
  if (!progress) return null;

  const pct = Math.round(progress.percent ?? 0);
  let title = 'Running Backtest...';
  if (progress.status === 'completed') {
    title = 'Backtest Complete';
  } else if (progress.status === 'error') {
    title = 'Backtest Failed';
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {progress.message && (
          <p className="mt-2 text-sm text-gray-600">{progress.message}</p>
        )}
        {progress.current_date && (
          <p className="mt-1 text-xs text-gray-500">Current date: {progress.current_date}</p>
        )}
        <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-200">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-300"
            style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
          />
        </div>
        <p className="mt-2 text-right text-xs font-medium text-gray-600">{pct}%</p>
      </div>
    </div>
  );
};

// Screen Results Panel Component
const ScreenResultsPanel = ({ result, screenName, formulas = [], selectionConfig, assetClass }) => {
  // State for watchlist saving (must be at top before any returns)
  const assetLabel = getAssetClassLabel(assetClass);
  const assetLabelSingular = getAssetClassSingular(assetClass);
  const [selectedWatchlistName, setSelectedWatchlistName] = useState(
    screenName ? `${screenName} - Selected` : `Selected ${assetLabel}`
  );
  const [unselectedWatchlistName, setUnselectedWatchlistName] = useState(
    screenName ? `${screenName} - Not Selected` : `Not Selected ${assetLabel}`
  );
  const [savingWatchlist, setSavingWatchlist] = useState(false);

  // Handle result shape: result?.screen_results || result
  const data = result?.screen_results || result;
  if (!data || !data.results) {
    return <div>No screen results available</div>;
  }

  const { screen_date, total_universe, valid_data_count, selected_count, selection_percent, weighting_method, weighting_method_effective, results } = data;

  // Determine what factors are displayed
  const primaryFactor = results.length > 0 ? results[0].factor_used : 'pe_ratio';
  const factorsDisplayed = results.length > 0 ? results[0].factors_displayed : [primaryFactor];

  // Use exactly the factors returned by the backend
  const effectiveFactorsDisplayed = factorsDisplayed;

  // Debug logging
  console.log('ScreenResultsPanel Debug:', {
    resultsCount: results.length,
    primaryFactor,
    factorsDisplayed,
    firstResult: results[0],
    firstResultKeys: results[0] ? Object.keys(results[0]) : []
  });

  // Helper functions for formatting
  const pct = (v) => (Number.isFinite(v) ? `${(v * 100).toFixed(2)}%` : 'N/A');
  const num = (v, d = 2) => (Number.isFinite(v) ? v.toFixed(d) : 'N/A');

  const parseNumeric = (val) => {
    if (typeof val === 'number') {
      return Number.isFinite(val) ? val : undefined;
    }
    if (typeof val === 'string') {
      const parsed = Number(val);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
  };

  // Helper function to get factor display info
  const getFactorDisplayInfo = (factorId) => {
    // If the backend returns a raw formula or an unknown id, show it verbatim and read from factor_value
    const fallback = (label) => {
      // If the raw label matches one of the user-defined formulas, prefer that friendly name
      const matchingFormula = formulas.find(f => f.formula === label);
      const friendly = matchingFormula ? matchingFormula.name : label;
      return {
        name: friendly || 'Factor',
        field: 'factor_value',
        format: (val) => num(val, 2)
      };
    };

    // Handle formula factors (e.g., "formula:1")
    if (factorId && factorId.startsWith('formula:')) {
      // Extract formula name from formulas prop
      const formulaId = factorId.replace('formula:', '');
      const formula = formulas.find(f => f.id === formulaId);
      const formulaName = formula ? formula.name : 'Custom Formula';
      return {
        name: formulaName,
        field: factorId,  // Use the factorId itself as the field name
        format: (val) => num(val, 2)
      };
    }

    const factorMap = {
      // Valuation factors
      'pe_ratio': { name: 'PE Ratio', field: 'pe_ratio', format: (val) => num(val, 2) },
      'pb_ratio': { name: 'PB Ratio', field: 'pb_ratio', format: (val) => num(val, 2) },
      'ps_ratio': { name: 'P/S Ratio', field: 'ps_ratio', format: (val) => num(val, 2) },
      'pcf_ratio': { name: 'P/CF Ratio', field: 'pcf_ratio', format: (val) => num(val, 2) },
      'pfcf_ratio': { name: 'P/FCF Ratio', field: 'pfcf_ratio', format: (val) => num(val, 2) },
      'ev_to_sales': { name: 'EV/Sales', field: 'ev_to_sales', format: (val) => num(val, 2) },
      'ev_to_ebitda': { name: 'EV/EBITDA', field: 'ev_to_ebitda', format: (val) => num(val, 2) },
      'earnings_yield': { name: 'Earnings Yield', field: 'earnings_yield', format: pct },
      'fcf_yield': { name: 'FCF Yield', field: 'fcf_yield', format: pct },
      'dividend_yield': { name: 'Dividend Yield', field: 'dividend_yield', format: pct },
      'ev_to_ebit': { name: 'EV/EBIT', field: 'ev_to_ebit', format: (val) => num(val, 2) },
      'ev_to_fcf': { name: 'EV/FCF', field: 'ev_to_fcf', format: (val) => num(val, 2) },
      'buyback_yield': { name: 'Buyback Yield', field: 'buyback_yield', format: pct },
      'shareholder_yield': { name: 'Shareholder Yield', field: 'shareholder_yield', format: pct },

      // Profitability factors
      'roe': { name: 'ROE', field: 'roe', format: pct },
      'roa': { name: 'ROA', field: 'roa', format: pct },
      'roic': { name: 'ROIC', field: 'roic', format: pct },
      'gross_margin': { name: 'Gross Margin', field: 'gross_margin', format: pct },
      'operating_margin': { name: 'Operating Margin', field: 'operating_margin', format: pct },
      'net_margin': { name: 'Net Margin', field: 'net_margin', format: pct },
      'income_quality': { name: 'Income Quality', field: 'income_quality', format: (val) => num(val, 2) },

      // Growth factors
      'revenue_growth': { name: 'Revenue Growth', field: 'revenue_growth', format: pct },

      // Leverage factors
      'debt_to_equity': { name: 'Debt-to-Equity', field: 'debt_to_equity', format: (val) => num(val, 2) },
      'debt_to_assets': { name: 'Debt-to-Assets', field: 'debt_to_assets', format: (val) => num(val, 2) },
      'net_debt_to_ebitda': { name: 'Net Debt/EBITDA', field: 'net_debt_to_ebitda', format: (val) => num(val, 2) },
      'interest_coverage': { name: 'Interest Coverage', field: 'interest_coverage', format: (val) => num(val, 2) },

      // Liquidity factors
      'current_ratio': { name: 'Current Ratio', field: 'current_ratio', format: (val) => num(val, 2) },
      'quick_ratio': { name: 'Quick Ratio', field: 'quick_ratio', format: (val) => num(val, 2) },
      'cash_ratio': { name: 'Cash Ratio', field: 'cash_ratio', format: (val) => num(val, 2) },

      // Efficiency factors
      'asset_turnover': { name: 'Asset Turnover', field: 'asset_turnover', format: (val) => num(val, 2) },
      'receivables_turnover': { name: 'Receivables Turnover', field: 'receivables_turnover', format: (val) => num(val, 2) },
      'inventory_turnover': { name: 'Inventory Turnover', field: 'inventory_turnover', format: (val) => num(val, 2) },
      'days_sales_outstanding': { name: 'DSO', field: 'days_sales_outstanding', format: (val) => Number.isFinite(val) ? `${val.toFixed(1)} days` : 'N/A' },
      'cash_conversion_cycle': { name: 'CCC', field: 'cash_conversion_cycle', format: (val) => Number.isFinite(val) ? `${val.toFixed(1)} days` : 'N/A' },

      // Payout factors
      'payout_ratio': { name: 'Payout Ratio', field: 'payout_ratio', format: pct },

      // Quality factors
      'piotroski_f_score': { name: 'F-Score', field: 'piotroski_f_score', format: (val) => num(val, 0) }
    };
    return factorMap[factorId] || fallback(factorId);
  };

  // Get info for all displayed factors
  const factorInfos = effectiveFactorsDisplayed.map(factor => ({
    id: factor,
    ...getFactorDisplayInfo(factor)
  }));

  // Separate selected and unselected assets
  const selectedStocks = results.filter(stock => stock.selected);
  const unselectedStocks = results.filter(stock => !stock.selected);

  const rankPickConfig = selectionConfig?.rankPick;
  const selectionMethod = rankPickConfig?.method;
  const selectionKValue = parseNumeric(rankPickConfig?.k);
  const percentFromResult = parseNumeric(selection_percent);
  const percentValue = percentFromResult ?? (selectionMethod && selectionMethod.endsWith('_pct') ? selectionKValue : undefined);

  const selectionSummaryLabel = (() => {
    if (!selectionMethod) {
      if (percentValue !== undefined) {
        return `Selected (${percentValue}%)`;
      }
      return 'Selected';
    }
    const direction = selectionMethod.startsWith('bottom') ? 'Bottom' : 'Top';
    const isPercentMethod = selectionMethod.endsWith('_pct');
    const value = isPercentMethod ? percentValue : selectionKValue;
    if (value !== undefined) {
      const suffix = isPercentMethod ? '%' : '';
      return `Selected (${direction} ${value}${suffix})`;
    }
    return `Selected (${direction})`;
  })();

  // Function to save watchlist
  const saveWatchlist = async (stocks, name, type) => {
    try {
      setSavingWatchlist(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4998';

      // Convert selections to the format expected by the watchlist API
      const symbols = stocks.map(stock => ({
        ticker: stock.ticker,
        company_name: stock.ticker,
        sector: 'Unknown',
        asset_class: normalizeAssetClass(assetClass)
      }));

      const response = await fetch(`${API_BASE}/api/watchlists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          name: name,
          description: `Created from ${assetLabelSingular} screen results (${type})`,
          asset_class: normalizeAssetClass(assetClass),
          symbols: symbols
        })
      });

      if (!response.ok) throw new Error('Failed to save watchlist');

      alert(`Watchlist "${name}" saved successfully with ${symbols.length} ${assetLabelSingular.toLowerCase()}${symbols.length === 1 ? '' : 's'}!`);
    } catch (error) {
      console.error('Error saving watchlist:', error);
      alert(`Error saving watchlist: ${error.message}`);
    } finally {
      setSavingWatchlist(false);
    }
  };

  return (
    <div className="screen-results space-y-6">
      {/* Summary Stats */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="font-semibold mb-3">Screen Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-gray-500">Screen Date</div>
            <div className="font-medium">{screen_date}</div>
          </div>
          <div>
            <div className="text-gray-500">Total Universe</div>
            <div className="font-medium">{total_universe}</div>
          </div>
          <div>
            <div className="text-gray-500">Valid Data</div>
            <div className="font-medium">{valid_data_count}</div>
          </div>
          <div>
            <div className="text-gray-500">{selectionSummaryLabel}</div>
            <div className="font-medium text-green-600">{selected_count}</div>
          </div>
        </div>
        <div className="mt-2 text-sm">
          <span className="text-gray-500">Weighting Method: </span>
          <span className="font-medium">{(weighting_method_effective || weighting_method) === 'equal' ? 'Equal Weight' : 'Market Cap Weight'}</span>
        </div>
      </div>

      {/* Selected Assets Table */}
      <div>
        <h3 className="font-semibold mb-3 text-green-600">Selected {assetLabel} ({selectedStocks.length})</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse border border-gray-300">
            <thead>
              <tr className="bg-green-50">
                <th className="border border-gray-300 px-3 py-2 text-left">Ticker</th>
                <th className="border border-gray-300 px-3 py-2 text-left">Price</th>
                {factorInfos.map(factor => (
                  <th key={factor.id} className="border border-gray-300 px-3 py-2 text-left">{factor.name}</th>
                ))}
                <th className="border border-gray-300 px-3 py-2 text-left">Weight</th>
                {(weighting_method_effective || weighting_method) === 'cap_weight' && (
                  <th className="border border-gray-300 px-3 py-2 text-left">Market Cap</th>
                )}
              </tr>
            </thead>
            <tbody>
              {selectedStocks.map((stock, index) => (
                <tr key={stock.ticker} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="border border-gray-300 px-3 py-2 font-medium">{stock.ticker}</td>
                  <td className="border border-gray-300 px-3 py-2">${stock.price?.toFixed(2) || 'N/A'}</td>
                  {factorInfos.map(factor => (
                    <td key={factor.id} className="border border-gray-300 px-3 py-2">{factor.format(stock[factor.field])}</td>
                  ))}
                  <td className="border border-gray-300 px-3 py-2 font-medium text-green-600">
                    {stock.weight_percent?.toFixed(2) || '0.00'}%
                  </td>
                  {(weighting_method_effective || weighting_method) === 'cap_weight' && (
                    <td className="border border-gray-300 px-3 py-2">
                      {stock.market_cap ? `$${(stock.market_cap / 1e9).toFixed(1)}B` : 'N/A'}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Save Selected Assets as Watchlist */}
        {selectedStocks.length > 0 && (
          <div className="mt-4 p-4 bg-green-50 rounded-lg">
            <h4 className="font-medium text-green-800 mb-2">Save Selected {assetLabel} as Watchlist</h4>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="block text-sm text-green-700 mb-1">Watchlist Name</label>
                <input
                  type="text"
                  value={selectedWatchlistName}
                  onChange={(e) => setSelectedWatchlistName(e.target.value)}
                  className="w-full px-3 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Enter watchlist name..."
                />
              </div>
              <button
                onClick={() => saveWatchlist(selectedStocks, selectedWatchlistName, 'selected')}
                disabled={!selectedWatchlistName.trim() || savingWatchlist}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingWatchlist ? 'Saving...' : `Save ${selectedStocks.length} ${assetLabel}`}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Unselected Assets (Top 20) */}
      {unselectedStocks.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3 text-gray-600">
            Not Selected {assetLabel} (Top 20 of {unselectedStocks.length})
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 px-3 py-2 text-left">Ticker</th>
                  <th className="border border-gray-300 px-3 py-2 text-left">Price</th>
                  {factorInfos.map(factor => (
                    <th key={factor.id} className="border border-gray-300 px-3 py-2 text-left">{factor.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {unselectedStocks.slice(0, 20).map((stock, index) => (
                  <tr key={stock.ticker} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border border-gray-300 px-3 py-2">{stock.ticker}</td>
                    <td className="border border-gray-300 px-3 py-2">${stock.price?.toFixed(2) || 'N/A'}</td>
                    {factorInfos.map(factor => (
                      <td key={factor.id} className="border border-gray-300 px-3 py-2">{factor.format(stock[factor.field])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Save Unselected Assets as Watchlist */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-800 mb-2">Save Top 20 Not Selected as Watchlist</h4>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="block text-sm text-gray-700 mb-1">Watchlist Name</label>
                <input
                  type="text"
                  value={unselectedWatchlistName}
                  onChange={(e) => setUnselectedWatchlistName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  placeholder="Enter watchlist name..."
                />
              </div>
              <button
                onClick={() => saveWatchlist(unselectedStocks.slice(0, 20), unselectedWatchlistName, 'not selected')}
                disabled={!unselectedWatchlistName.trim() || savingWatchlist}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingWatchlist ? 'Saving...' : `Save Top 20 ${assetLabel}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PortfolioScreenResults = ({ result, screenName, portfolioElements = [] }) => {
  const data = result?.screen_results || result;
  const elements = Array.isArray(data?.elements) ? data.elements : [];
  if (elements.length === 0) {
    return (
      <ScreenResultsPanel
        result={data}
        screenName={screenName}
        formulas={portfolioElements[0]?.formulas || []}
        selectionConfig={portfolioElements[0]?.selection || cloneSelectionConfig(DEFAULT_SELECTION_V2)}
        assetClass={portfolioElements[0]?.asset_class}
      />
    );
  }

  return (
    <div className="space-y-6">
      {elements.map((element, index) => {
        const matched = portfolioElements.find(el => el.id === element.id) || portfolioElements[index];
        const elementName = element.name || matched?.name || `Element ${index + 1}`;
        const elementScreenName = screenName ? `${screenName} - ${elementName}` : elementName;
        const elementAssetClass = element.asset_class || matched?.asset_class || matched?.universe?.asset_class;
        return (
          <Card key={element.id || elementName}>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{elementName}</CardTitle>
              {Number.isFinite(element.weight) && (
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Weight {roundWeight(element.weight)}%
                </span>
              )}
            </CardHeader>
            <CardContent>
              <ScreenResultsPanel
                result={element}
                screenName={elementScreenName}
                formulas={matched?.formulas || []}
                selectionConfig={matched?.selection || cloneSelectionConfig(DEFAULT_SELECTION_V2)}
                assetClass={elementAssetClass}
              />
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// Mock data for factors
const MOCK_FACTORS = [
  // Existing Valuation Factors
  {
    id: 'pe_ratio',
    name: 'PE Ratio',
    description: 'Price-to-earnings ratio',
    formula: 'price / eps.ttm(0)',
    category: 'Valuation',
    better: 'lower',
    activated: false
  },
  {
    id: 'pb_ratio',
    name: 'PB Ratio',
    description: 'Price-to-book ratio',
    formula: 'price / book_value_per_share.mrq(0)',
    category: 'Valuation',
    better: 'lower',
    activated: false
  },
  // New Valuation Factors
  {
    id: 'ps_ratio',
    name: 'P/S Ratio',
    description: 'Price-to-sales ratio',
    formula: 'price / (revenue.ttm(0) / shares.mrq(0))',
    category: 'Valuation',
    better: 'lower',
    activated: false
  },
  {
    id: 'pcf_ratio',
    name: 'P/CF Ratio',
    description: 'Price-to-cash flow ratio',
    formula: 'price / operating_cash_flow_per_share',
    category: 'Valuation',
    better: 'lower',
    activated: false
  },
  {
    id: 'pfcf_ratio',
    name: 'P/FCF Ratio',
    description: 'Price-to-free cash flow ratio',
    formula: 'price / free_cash_flow_per_share',
    category: 'Valuation',
    better: 'lower',
    activated: false
  },
  {
    id: 'ev_to_sales',
    name: 'EV/Sales',
    description: 'Enterprise value to sales ratio',
    formula: 'enterprise_value / revenue',
    category: 'Valuation',
    better: 'lower',
    activated: false
  },
  {
    id: 'ev_to_ebitda',
    name: 'EV/EBITDA',
    description: 'Enterprise value to EBITDA ratio',
    formula: 'enterprise_value / ebitda',
    category: 'Valuation',
    better: 'lower',
    activated: false
  },
  {
    id: 'earnings_yield',
    name: 'Earnings Yield',
    description: 'Inverse of P/E ratio',
    formula: 'eps / price',
    category: 'Valuation',
    better: 'higher',
    activated: false
  },
  {
    id: 'fcf_yield',
    name: 'FCF Yield',
    description: 'Free cash flow yield',
    formula: 'fcf / market_cap',
    category: 'Valuation',
    better: 'higher',
    activated: false
  },
  {
    id: 'dividend_yield',
    name: 'Dividend Yield',
    description: 'Annual dividends per share / price',
    formula: 'dividend_per_share / price',
    category: 'Valuation',
    better: 'higher',
    activated: false
  },
  {
    id: 'ev_to_ebit',
    name: 'EV/EBIT',
    description: 'Enterprise value to EBIT ratio',
    formula: 'enterprise_value / ebit',
    category: 'Valuation',
    better: 'lower',
    activated: false
  },
  {
    id: 'ev_to_fcf',
    name: 'EV/FCF',
    description: 'Enterprise value to free cash flow ratio',
    formula: 'enterprise_value / fcf',
    category: 'Valuation',
    better: 'lower',
    activated: false
  },
  {
    id: 'buyback_yield',
    name: 'Buyback Yield',
    description: 'Share repurchases / market cap',
    formula: '-share_repurchases / market_cap',
    category: 'Valuation',
    better: 'higher',
    activated: false
  },
  {
    id: 'shareholder_yield',
    name: 'Shareholder Yield',
    description: 'Total cash return to shareholders',
    formula: 'dividend_yield + buyback_yield (cash-based)',
    category: 'Valuation',
    better: 'higher',
    activated: false
  },

  // Existing Profitability Factors
  {
    id: 'roe',
    name: 'ROE',
    description: 'Return on equity',
    formula: 'net_income / shareholders_equity',
    category: 'Profitability',
    better: 'higher',
    activated: false
  },
  {
    id: 'roa',
    name: 'ROA',
    description: 'Return on assets',
    formula: 'net_income / total_assets',
    category: 'Profitability',
    better: 'higher',
    activated: false
  },
  // New Profitability Factors
  {
    id: 'roic',
    name: 'ROIC',
    description: 'Return on invested capital',
    formula: 'nopat / invested_capital',
    category: 'Profitability',
    better: 'higher',
    activated: false
  },
  {
    id: 'gross_margin',
    name: 'Gross Margin',
    description: 'Gross profit margin',
    formula: 'gross_profit / revenue',
    category: 'Profitability',
    better: 'higher',
    activated: false
  },
  {
    id: 'operating_margin',
    name: 'Operating Margin',
    description: 'Operating profit margin',
    formula: 'operating_income / revenue',
    category: 'Profitability',
    better: 'higher',
    activated: false
  },
  {
    id: 'net_margin',
    name: 'Net Margin',
    description: 'Net profit margin',
    formula: 'net_income / revenue',
    category: 'Profitability',
    better: 'higher',
    activated: false
  },
  {
    id: 'income_quality',
    name: 'Income Quality',
    description: 'Cash flow to net income ratio',
    formula: 'operating_cash_flow / net_income',
    category: 'Profitability',
    better: 'higher',
    activated: false
  },

  // Growth Factors
  {
    id: 'revenue_growth',
    name: 'Revenue Growth',
    description: 'Year-over-year revenue growth',
    formula: '(revenue - revenue_prev_year) / revenue_prev_year',
    category: 'Growth',
    better: 'higher',
    activated: false
  },

  // Existing Leverage Factors
  {
    id: 'debt_to_equity',
    name: 'Debt-to-Equity',
    description: 'Total debt to shareholders equity',
    formula: 'total_debt / shareholders_equity',
    category: 'Leverage',
    better: 'lower',
    activated: false
  },
  // New Leverage Factors
  {
    id: 'debt_to_assets',
    name: 'Debt-to-Assets',
    description: 'Total debt to total assets',
    formula: 'total_debt / total_assets',
    category: 'Leverage',
    better: 'lower',
    activated: false
  },
  {
    id: 'net_debt_to_ebitda',
    name: 'Net Debt/EBITDA',
    description: 'Net debt to EBITDA ratio',
    formula: 'net_debt / ebitda',
    category: 'Leverage',
    better: 'lower',
    activated: false
  },
  {
    id: 'interest_coverage',
    name: 'Interest Coverage',
    description: 'Ability to pay interest',
    formula: 'ebit / interest_expense',
    category: 'Leverage',
    better: 'higher',
    activated: false
  },

  // Liquidity Factors
  {
    id: 'current_ratio',
    name: 'Current Ratio',
    description: 'Current assets to current liabilities',
    formula: 'current_assets / current_liabilities',
    category: 'Liquidity',
    better: 'higher',
    activated: false
  },
  {
    id: 'quick_ratio',
    name: 'Quick Ratio',
    description: 'Quick assets to current liabilities',
    formula: '(current_assets - inventory) / current_liabilities',
    category: 'Liquidity',
    better: 'higher',
    activated: false
  },
  {
    id: 'cash_ratio',
    name: 'Cash Ratio',
    description: 'Cash to current liabilities',
    formula: 'cash / current_liabilities',
    category: 'Liquidity',
    better: 'higher',
    activated: false
  },

  // Efficiency Factors
  {
    id: 'asset_turnover',
    name: 'Asset Turnover',
    description: 'Revenue per dollar of assets',
    formula: 'revenue / avg_total_assets',
    category: 'Efficiency',
    better: 'higher',
    activated: false
  },
  {
    id: 'receivables_turnover',
    name: 'Receivables Turnover',
    description: 'How quickly receivables are collected',
    formula: 'revenue / avg_receivables',
    category: 'Efficiency',
    better: 'higher',
    activated: false
  },
  {
    id: 'inventory_turnover',
    name: 'Inventory Turnover',
    description: 'How quickly inventory is sold',
    formula: 'cogs / avg_inventory',
    category: 'Efficiency',
    better: 'higher',
    activated: false
  },
  {
    id: 'days_sales_outstanding',
    name: 'Days Sales Outstanding',
    description: 'Days to collect receivables',
    formula: '365 / receivables_turnover',
    category: 'Efficiency',
    better: 'lower',
    activated: false
  },
  {
    id: 'cash_conversion_cycle',
    name: 'Cash Conversion Cycle',
    description: 'Days to convert inventory to cash',
    formula: 'dso + dio - dpo',
    category: 'Efficiency',
    better: 'lower',
    activated: false
  },

  // Payout Factors
  {
    id: 'payout_ratio',
    name: 'Payout Ratio',
    description: 'Dividends as % of earnings',
    formula: 'dividends / net_income',
    category: 'Payout',
    better: 'moderate',
    activated: false
  },

  // Quality/Composite Factors
  {
    id: 'piotroski_f_score',
    name: 'Piotroski F-Score',
    description: 'Financial strength score (6/9 components: 0-6)',
    formula: 'ROA>0, CFO>0, ΔROA>0, CFO>NI, ΔLev<0, ΔCR>0',
    category: 'Quality',
    better: 'higher',
    activated: false
  }
];

const ScreenBuilder = () => {
  // URL search params for backtest loading
  const location = useLocation();

  // Get premium status and billing tier from auth context
  const { isPremium, billingTier } = useAuth();

  // Register reset function with ScreenBuilderContext + dev mode flags
  const {
    setResetScreensCallback,
    devModeEnabled,
    setDevModeEnabled,
    devJsonPayload,
    setDevJsonPayload
  } = useScreenBuilder();

  // Ref to track if we've already processed a backtest to prevent React StrictMode double execution
  const processedBacktestRef = useRef(null);
  const processedQueryScreenRef = useRef(false);
  const processedQueryResultRef = useRef(false);
  const processedQueryDraftRef = useRef(null);

  // Ref to track if we're currently loading a screen (to avoid marking as unsaved during load)
  const isLoadingScreenRef = useRef(false);

  // Screen management state
  const [currentScreen, setCurrentScreen] = useState(null);
  const [currentScreenId, setCurrentScreenId] = useState(null);
  const [screenName, setScreenName] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Strategy state management
  const initialElement = useMemo(
    () => createPortfolioElement({ asset_class: DEFAULT_ASSET_CLASS, name: 'Element 1' }),
    []
  );
  const [portfolioElements, setPortfolioElements] = useState(() => [initialElement]);
  const [activeElementId, setActiveElementId] = useState(() => initialElement.id);
  const [activeStep, setActiveStep] = useState('formula');
  const [factors, setFactors] = useState(MOCK_FACTORS);
  const [formulas, setFormulas] = useState(() => initialElement.formulas || []);
  const [watchlists, setWatchlists] = useState([]);
  const [loadingWatchlists, setLoadingWatchlists] = useState(false);
  const [showWatchlistModal, setShowWatchlistModal] = useState(false);
  const [watchlistDraftName, setWatchlistDraftName] = useState('');
  const [watchlistDraftDescription, setWatchlistDraftDescription] = useState('');
  const [watchlistDraftSymbols, setWatchlistDraftSymbols] = useState([]);
  const [watchlistBulkInput, setWatchlistBulkInput] = useState('');
  const [watchlistMissingSymbols, setWatchlistMissingSymbols] = useState([]);
  const [watchlistSearchTerm, setWatchlistSearchTerm] = useState('');
  const [watchlistSymbols, setWatchlistSymbols] = useState([]);
  const [watchlistSymbolsLoading, setWatchlistSymbolsLoading] = useState(false);
  const [watchlistSymbolsError, setWatchlistSymbolsError] = useState(null);
  const [watchlistModalMessage, setWatchlistModalMessage] = useState(null);
  const [watchlistSaving, setWatchlistSaving] = useState(false);
  const [assetSymbols, setAssetSymbols] = useState([]);
  const [loadingAssetSymbols, setLoadingAssetSymbols] = useState(false);
  const [assetSymbolsError, setAssetSymbolsError] = useState(null);
  const [assetSymbolQuery, setAssetSymbolQuery] = useState('');
  const assetSymbolsAutoSelectedRef = useRef(false);
  const [universeConfig, setUniverseConfig] = useState(() => initialElement.universe);
  const [selectionConfig, setSelectionConfig] = useState(() => initialElement.selection || cloneSelectionConfig(DEFAULT_SELECTION_V2));
  const [backtestConfigs, setBacktestConfigs] = useState([]);
  const [selectedBacktestConfigId, setSelectedBacktestConfigId] = useState(null);
  const [loadingBacktestConfigs, setLoadingBacktestConfigs] = useState(false);
  const [backtestConfigsError, setBacktestConfigsError] = useState(null);
  const [optimizerConfig, setOptimizerConfig] = useState({
    start: '0',
    end: '100',
    step: '5',
    target: 'total_return',
    direction: 'max'
  });
  const [screenRunTiming, setScreenRunTiming] = useState('now');
  const [screenRunDate, setScreenRunDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [weightFormulaValidation, setWeightFormulaValidation] = useState({});
  const [weightFormulaDebug, setWeightFormulaDebug] = useState({});
  const [weightFormulaDebugLoading, setWeightFormulaDebugLoading] = useState({});
  const weightValidationTimersRef = useRef({});
  const weightValidationRequestRef = useRef({});
  const lastWeightFormulaRef = useRef({});
  const totalPortfolioWeight = useMemo(() => {
    let total = 0;
    for (const element of portfolioElements) {
      const value = getElementWeightValue(element);
      if (value === null) {
        return null;
      }
      total += sanitizeWeight(value);
    }
    return total;
  }, [portfolioElements]);
  const optimizerVars = useMemo(
    () => collectOptimizerVarsFromElements(portfolioElements),
    [portfolioElements]
  );
  const hasOptimizerVar = optimizerVars.size > 0;
  const optimizerAllowed = (billingTier || 'free') !== 'free';
  const optimizerStepInfo = useMemo(() => {
    if (!hasOptimizerVar) {
      return { count: 0, error: null };
    }
    const start = Number(optimizerConfig.start);
    const end = Number(optimizerConfig.end);
    const step = Number(optimizerConfig.step);
    if (!Number.isFinite(start) || !Number.isFinite(end) || !Number.isFinite(step)) {
      return { count: 0, error: 'Enter numeric start, end, and step values.' };
    }
    if (step === 0) {
      return { count: 0, error: 'Step must be non-zero.' };
    }
    if (start === end) {
      return { count: 1, error: null };
    }
    const stepMag = Math.abs(step);
    const delta = Math.abs(end - start);
    const count = Math.floor(delta / stepMag + 1e-9) + 1;
    if (count > OPTIMIZER_MAX_STEPS) {
      return { count, error: `Max ${OPTIMIZER_MAX_STEPS} steps allowed.` };
    }
    return { count, error: null };
  }, [hasOptimizerVar, optimizerConfig]);

  useEffect(() => {
    if (!activeElementId) return;
    const assetClass = normalizeAssetClass(universeConfig?.asset_class);
    setPortfolioElements(prev => prev.map(el => {
      if (el.id !== activeElementId) return el;
      return {
        ...el,
        asset_class: assetClass,
        universe: {
          ...(universeConfig || {}),
          asset_class: assetClass
        },
        formulas: Array.isArray(formulas) ? formulas : [],
        selection: selectionConfig || cloneSelectionConfig(DEFAULT_SELECTION_V2)
      };
    }));
  }, [activeElementId, universeConfig, formulas, selectionConfig]);

  const selectPortfolioElement = useCallback((elementId, { jumpToUniverse = false } = {}) => {
    const element = portfolioElements.find(el => el.id === elementId);
    if (!element) return;
    isLoadingScreenRef.current = true;
    setActiveElementId(elementId);
    setUniverseConfig(element.universe || createUniverseDefaults(element.asset_class));
    setFormulas(Array.isArray(element.formulas) ? element.formulas : []);
    setSelectionConfig(element.selection || cloneSelectionConfig(DEFAULT_SELECTION_V2));
    if (jumpToUniverse) {
      setActiveStep('universe');
    }
    setTimeout(() => {
      isLoadingScreenRef.current = false;
    }, 100);
  }, [portfolioElements]);

  const updatePortfolioElement = useCallback((elementId, updates) => {
    setPortfolioElements(prev => prev.map(el => (
      el.id === elementId ? { ...el, ...updates } : el
    )));
  }, []);

  const addPortfolioElement = useCallback(() => {
    const assetClass = normalizeAssetClass(universeConfig?.asset_class);
    const newElement = {
      ...createPortfolioElement({ asset_class: assetClass }),
      name: `Element ${portfolioElements.length + 1}`
    };
    setPortfolioElements(prev => normalizePortfolioWeights([...prev, newElement], { mode: 'equal' }));
    isLoadingScreenRef.current = true;
    setActiveElementId(newElement.id);
    setUniverseConfig(newElement.universe);
    setFormulas(newElement.formulas || []);
    setSelectionConfig(newElement.selection || cloneSelectionConfig(DEFAULT_SELECTION_V2));
    setActiveStep('universe');
    setTimeout(() => {
      isLoadingScreenRef.current = false;
    }, 100);
  }, [portfolioElements.length, universeConfig]);

  const removePortfolioElement = useCallback((elementId) => {
    setPortfolioElements(prev => {
      const remaining = prev.filter(el => el.id !== elementId);
      if (remaining.length === 0) {
        const fallback = createPortfolioElement({ asset_class: DEFAULT_ASSET_CLASS });
        setActiveElementId(fallback.id);
        setUniverseConfig(fallback.universe);
        setFormulas(fallback.formulas || []);
        setSelectionConfig(fallback.selection || cloneSelectionConfig(DEFAULT_SELECTION_V2));
        return [fallback];
      }
      if (elementId === activeElementId) {
        const next = remaining[0];
        setActiveElementId(next.id);
        setUniverseConfig(next.universe || createUniverseDefaults(next.asset_class));
        setFormulas(Array.isArray(next.formulas) ? next.formulas : []);
        setSelectionConfig(next.selection || cloneSelectionConfig(DEFAULT_SELECTION_V2));
      }
      return normalizePortfolioWeights(remaining, { mode: 'normalize' });
    });
  }, [activeElementId]);

  const toggleUniverseSymbol = useCallback((ticker) => {
    const normalizedTicker = String(ticker || '').trim().toUpperCase();
    if (!normalizedTicker) return;
    setUniverseConfig(prev => {
      const current = new Set(normalizeSymbolList(prev.symbols));
      if (current.has(normalizedTicker)) {
        current.delete(normalizedTicker);
      } else {
        current.add(normalizedTicker);
      }
      return {
        ...prev,
        asset_class: normalizeAssetClass(prev.asset_class),
        symbols: Array.from(current),
        type: prev.type || 'custom'
      };
    });
  }, []);

  const setUniverseSymbols = useCallback((symbols) => {
    const nextSymbols = normalizeSymbolList(symbols);
    setUniverseConfig(prev => ({
      ...prev,
      asset_class: normalizeAssetClass(prev.asset_class),
      symbols: nextSymbols,
      type: prev.type || 'custom'
    }));
  }, []);

  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState(undefined);
  const [shareTargets, setShareTargets] = useState({ screenResultId: null, backtestId: null });
  const [shareFeedback, setShareFeedback] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [runError, setRunError] = useState(null);
  const [devPanelError, setDevPanelError] = useState(null);
  const [defaultsBanner, setDefaultsBanner] = useState(undefined);
  const [showResults, setShowResults] = useState(false);
  const activeShareId = result
    ? (result?.is_screen ? shareTargets.screenResultId : shareTargets.backtestId)
    : null;
  const canShareResults = Boolean(activeShareId);
  const isOptimizationResult = Boolean(result?.optimization || result?.is_optimization);

  const todayStr = new Date().toISOString().split('T')[0];
  const screenDateLimits = useMemo(() => {
    const tier = billingTier || 'free';
    const tierLabel = getTierDisplayName(tier);
    const maxYears = getMaxBacktestYears(tier);
    const earliestDate = getEarliestScreenDate(new Date(), tier);
    return {
      tier,
      tierLabel,
      maxYears,
      earliestDateStr: earliestDate.toISOString().split('T')[0]
    };
  }, [billingTier]);
  const { tierLabel: screenTierLabel, maxYears: screenMaxYears, earliestDateStr } = screenDateLimits;

  const [backtestProgress, setBacktestProgress] = useState(null);
  const progressIntervalRef = useRef(null);
  const progressTokenRef = useRef(null);

  const stopProgressPolling = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
      console.debug('[ProgressPoll] stopped interval');
    }
  }, []);

  const pollProgressOnce = useCallback(async (token) => {
    if (!token) return null;
    console.debug('[ProgressPoll] poll once', token);
    const data = await getBacktestProgress(token);
    if (!data) {
      console.debug('[ProgressPoll] no data returned', token);
      return null;
    }

    setBacktestProgress((prev) => ({ ...(prev || {}), ...data }));

    if (data.status === 'completed' || data.status === 'error') {
      stopProgressPolling();
    }
    return data;
  }, [stopProgressPolling]);

  const startProgressPolling = useCallback((token) => {
    if (!token) return;
    console.debug('[ProgressPoll] start interval', token);
    stopProgressPolling();
    pollProgressOnce(token);
    progressIntervalRef.current = setInterval(() => {
      pollProgressOnce(token);
    }, 2000);
  }, [pollProgressOnce, stopProgressPolling]);

  const handleShareResult = useCallback(async () => {
    if (!result) return;
    const isScreen = Boolean(result?.is_screen);
    const resourceId = isScreen ? shareTargets.screenResultId : shareTargets.backtestId;
    if (!resourceId) {
      setShareFeedback({
        isError: true,
        message: 'Run and save results before sharing.'
      });
      return;
    }
    setShareFeedback(null);
    try {
      setSharing(true);
      const shareId = await createShareLink(
        isScreen ? 'screen_result' : 'backtest',
        resourceId
      );
      const shareUrl = `${window.location.origin}/share/${shareId}`;
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        window.prompt('Copy share link:', shareUrl);
      }
      setShareFeedback({ message: 'Share link copied to clipboard.' });
    } catch (err) {
      setShareFeedback({
        isError: true,
        message: err.message || 'Failed to create share link'
      });
    } finally {
      setSharing(false);
    }
  }, [result, shareTargets]);

  useEffect(() => {
    return () => {
      stopProgressPolling();
    };
  }, [stopProgressPolling]);

  // Onboarding state: 0 = blink category, 1 = blink first formula, 2 = blink Selection step, 3 = blink Ranking Formula, 4 = done
  const [onboardingStep, setOnboardingStep] = useState(4); // Default to disabled

  // Fetch user settings from backend
  useEffect(() => {
    const fetchUserSettings = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const response = await fetch(`${API_BASE}/api/user/settings`, {
          headers: { Authorization: `Bearer ${session.access_token}` }
        });

        if (response.ok) {
          const data = await response.json();
          // Set onboarding step based on backend setting
          setOnboardingStep(data.settings.onboarding_enabled ? 0 : 4);
        }
      } catch (err) {
        console.error('Error fetching user settings:', err);
      }
    };

    fetchUserSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBacktestConfigs = useCallback(async () => {
    try {
      setLoadingBacktestConfigs(true);
      setBacktestConfigsError(null);
      const configs = await fetchBacktestConfigs();
      setBacktestConfigs(configs);
      setSelectedBacktestConfigId((prev) => {
        if (prev && configs.some((cfg) => cfg.id === prev)) {
          return prev;
        }
        return configs.find((cfg) => cfg.is_default)?.id || configs[0]?.id || null;
      });
    } catch (err) {
      console.error('Error fetching backtest configs:', err);
      setBacktestConfigsError(err.message || 'Failed to load backtest configurations');
    } finally {
      setLoadingBacktestConfigs(false);
    }
  }, []);

  useEffect(() => {
    loadBacktestConfigs();
  }, [loadBacktestConfigs]);

  // API base URL
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4998';

  const scheduleWeightFormulaValidation = useCallback((elementId, formulaRaw) => {
    const formula = normalizeWeightFormulaInput(formulaRaw).trim();
    if (weightValidationTimersRef.current[elementId]) {
      clearTimeout(weightValidationTimersRef.current[elementId]);
    }
    if (!formula) {
      setWeightFormulaValidation((prev) => ({ ...prev, [elementId]: null }));
      setWeightFormulaDebug((prev) => ({ ...prev, [elementId]: null }));
      setWeightFormulaDebugLoading((prev) => ({ ...prev, [elementId]: false }));
      return;
    }
    setWeightFormulaDebug((prev) => ({ ...prev, [elementId]: null }));
    setWeightFormulaDebugLoading((prev) => ({ ...prev, [elementId]: false }));
    lastWeightFormulaRef.current[elementId] = formula;
    const requestId = (weightValidationRequestRef.current[elementId] || 0) + 1;
    weightValidationRequestRef.current[elementId] = requestId;
    weightValidationTimersRef.current[elementId] = setTimeout(async () => {
      try {
        const response = await fetch(`${API_BASE}/api/formulas/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ formula })
        });
        const data = await response.json();
        if (weightValidationRequestRef.current[elementId] !== requestId) return;
        const result = response.ok && data.status === 'success'
          ? data
          : { valid: false, error: data.message || 'Validation failed.' };
        setWeightFormulaValidation((prev) => ({ ...prev, [elementId]: result }));
        lastWeightFormulaRef.current[elementId] = formula;
        if (!result.valid) {
          setWeightFormulaDebug((prev) => ({ ...prev, [elementId]: null }));
          setWeightFormulaDebugLoading((prev) => ({ ...prev, [elementId]: false }));
          return;
        }
        setWeightFormulaDebugLoading((prev) => ({ ...prev, [elementId]: true }));
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (weightValidationRequestRef.current[elementId] !== requestId) return;
          if (!session) {
            setWeightFormulaDebug((prev) => ({
              ...prev,
              [elementId]: { error: 'Sign in to preview a live value.' }
            }));
            return;
          }
          const debugResponse = await fetch(`${API_BASE}/api/formulas/debug`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.access_token}`
            },
            body: JSON.stringify({ formula })
          });
          const debugData = await debugResponse.json();
          if (weightValidationRequestRef.current[elementId] !== requestId) return;
          if (!debugResponse.ok || debugData.status !== 'success') {
            setWeightFormulaDebug((prev) => ({
              ...prev,
              [elementId]: { error: debugData.message || 'Failed to load debug value.' }
            }));
          } else {
            setWeightFormulaDebug((prev) => ({ ...prev, [elementId]: debugData }));
          }
        } catch (err) {
          if (weightValidationRequestRef.current[elementId] !== requestId) return;
          setWeightFormulaDebug((prev) => ({
            ...prev,
            [elementId]: { error: 'Failed to load debug value.' }
          }));
        } finally {
          if (weightValidationRequestRef.current[elementId] === requestId) {
            setWeightFormulaDebugLoading((prev) => ({ ...prev, [elementId]: false }));
          }
        }
      } catch (err) {
        if (weightValidationRequestRef.current[elementId] !== requestId) return;
        setWeightFormulaValidation((prev) => ({
          ...prev,
          [elementId]: { valid: false, error: 'Validation failed.' }
        }));
        setWeightFormulaDebug((prev) => ({ ...prev, [elementId]: null }));
        setWeightFormulaDebugLoading((prev) => ({ ...prev, [elementId]: false }));
      }
    }, 350);
  }, [API_BASE]);

  useEffect(() => {
    portfolioElements.forEach((element) => {
      const formula = normalizeWeightFormulaInput(element.weight_formula || '').trim();
      if (!formula) {
        if (lastWeightFormulaRef.current[element.id]) {
          lastWeightFormulaRef.current[element.id] = '';
        }
        setWeightFormulaValidation((prev) => ({ ...prev, [element.id]: null }));
        setWeightFormulaDebug((prev) => ({ ...prev, [element.id]: null }));
        setWeightFormulaDebugLoading((prev) => ({ ...prev, [element.id]: false }));
        return;
      }
      if (lastWeightFormulaRef.current[element.id] === formula) {
        return;
      }
      scheduleWeightFormulaValidation(element.id, formula);
    });
  }, [portfolioElements, scheduleWeightFormulaValidation]);

  useEffect(() => {
    const timers = weightValidationTimersRef.current;
    return () => {
      Object.values(timers).forEach((timer) => clearTimeout(timer));
    };
  }, []);

  const selectedBacktestConfigRecord = useMemo(
    () => backtestConfigs.find((cfg) => cfg.id === selectedBacktestConfigId) || null,
    [backtestConfigs, selectedBacktestConfigId]
  );
  const selectedBacktestConfig = selectedBacktestConfigRecord?.config || null;
  const selectedBacktestRunConfig = selectedBacktestConfig?.backtest || selectedBacktestConfig?.run || null;

  const hydrateBuilderFromStrategyConfig = useCallback((config = {}, options = {}) => {
    // Mark that we're loading to prevent unsaved changes flag during hydration
    isLoadingScreenRef.current = true;

    const elements = resolvePortfolioElements(config);
    syncPortfolioCounter(elements);
    const primaryElement = elements[0] || createPortfolioElement({ asset_class: DEFAULT_ASSET_CLASS });

    setPortfolioElements(elements);
    setActiveElementId(primaryElement.id);
    setUniverseConfig(primaryElement.universe);

    setFactors(config.factors || MOCK_FACTORS);

    setFormulas(primaryElement.formulas || []);
    setSelectionConfig(primaryElement.selection || cloneSelectionConfig(DEFAULT_SELECTION_V2));

    if (options.screenName !== undefined) {
      setScreenName(options.screenName);
    }
    if (options.currentScreen !== undefined) {
      setCurrentScreen(options.currentScreen);
    }
    if (options.currentScreenId !== undefined) {
      setCurrentScreenId(options.currentScreenId);
    }

    setShowResults(false);
    setActiveStep('formula');
    setHasUnsavedChanges(false);

    // Reset loading flag after a short delay to ensure all state updates are processed
    setTimeout(() => {
      isLoadingScreenRef.current = false;
    }, 100);
  }, []);

  // Navigation guard function
  const confirmNavigation = useCallback(() => {
    if (hasUnsavedChanges) {
      return window.confirm('You have unsaved changes. Do you want to leave without saving?');
    }
    return true;
  }, [hasUnsavedChanges]);

  const newScreen = useCallback(() => {
    // Check for unsaved changes before creating a new screen
    if (!confirmNavigation()) {
      return;
    }

    // Mark that we're loading to prevent unsaved changes flag
    isLoadingScreenRef.current = true;

    // Reset to default values
    const freshElement = createPortfolioElement({ asset_class: DEFAULT_ASSET_CLASS });
    setFactors(MOCK_FACTORS);
    setPortfolioElements([freshElement]);
    setActiveElementId(freshElement.id);
    setFormulas([]);
    setUniverseConfig(freshElement.universe);
    setSelectionConfig(freshElement.selection || cloneSelectionConfig(DEFAULT_SELECTION_V2));
    setSelectedBacktestConfigId(
      backtestConfigs.find((cfg) => cfg.is_default)?.id || backtestConfigs[0]?.id || null
    );
    setCurrentScreen(null);
    setCurrentScreenId(null);
    setScreenName(''); // Will be auto-generated by useEffect
    setActiveStep('formula');
    setShowResults(false);
    setHasUnsavedChanges(false);

    // Reset loading flag after a short delay to ensure all state updates are processed
    setTimeout(() => {
      isLoadingScreenRef.current = false;
    }, 100);
  }, [backtestConfigs, confirmNavigation]);

  // Track changes to mark as unsaved
  useEffect(() => {
    // Don't mark as unsaved if we haven't loaded a screen yet or if we're currently loading
    if (isLoadingScreenRef.current) return;

    setHasUnsavedChanges(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formulas, universeConfig, selectionConfig, screenName, portfolioElements]);

  // Warn before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Register reset callback with ScreenBuilderContext
  useEffect(() => {
    const resetCallback = () => {
      newScreen();
    };

    setResetScreensCallback(() => resetCallback);
  }, [newScreen, setResetScreensCallback]);

  const fetchWatchlists = useCallback(async () => {
    try {
      setLoadingWatchlists(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`${API_BASE}/api/watchlists`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const normalizedWatchlists = (data.watchlists || []).map((watchlist) => ({
          ...watchlist,
          asset_class: normalizeAssetClass(watchlist.asset_class)
        }));
        setWatchlists(normalizedWatchlists);
      }
    } catch (error) {
      console.error('Failed to fetch watchlists:', error);
    } finally {
      setLoadingWatchlists(false);
    }
  }, [API_BASE]);

  // Fetch watchlists for universe dropdown
  useEffect(() => {
    fetchWatchlists();
  }, [fetchWatchlists]);

  useEffect(() => {
    if (!showWatchlistModal) return;
    const assetClass = normalizeAssetClass(universeConfig?.asset_class);
    if (assetClass !== 'stocks' && assetSymbols.length > 0) {
      setWatchlistSymbols(assetSymbols);
      setWatchlistSymbolsError(null);
      setWatchlistSymbolsLoading(false);
      return;
    }
    let cancelled = false;
    const fetchSymbols = async () => {
      try {
        setWatchlistSymbolsLoading(true);
        setWatchlistSymbolsError(null);
        const response = await fetch(`${API_BASE}/api/symbols?asset_class=${encodeURIComponent(assetClass)}`);
        if (!response.ok) throw new Error('Failed to fetch symbols');
        const data = await response.json();
        if (!cancelled) {
          setWatchlistSymbols(data.symbols || []);
        }
      } catch (error) {
        if (!cancelled) {
          setWatchlistSymbolsError(error.message || 'Failed to fetch symbols');
        }
      } finally {
        if (!cancelled) {
          setWatchlistSymbolsLoading(false);
        }
      }
    };
    fetchSymbols();
    return () => {
      cancelled = true;
    };
  }, [API_BASE, assetSymbols, showWatchlistModal, universeConfig?.asset_class]);

  useEffect(() => {
    const assetClass = normalizeAssetClass(universeConfig?.asset_class);
    assetSymbolsAutoSelectedRef.current = false;
    setAssetSymbolQuery('');
    if (assetClass === 'stocks') {
      setAssetSymbols([]);
      setLoadingAssetSymbols(false);
      setAssetSymbolsError(null);
      return;
    }
    let cancelled = false;
    const fetchSymbols = async () => {
      try {
        setLoadingAssetSymbols(true);
        setAssetSymbolsError(null);
        const response = await fetch(`${API_BASE}/api/symbols?asset_class=${encodeURIComponent(assetClass)}`);
        if (!response.ok) throw new Error('Failed to fetch symbols');
        const data = await response.json();
        if (!cancelled) {
          setAssetSymbols(data.symbols || []);
        }
      } catch (error) {
        if (!cancelled) {
          setAssetSymbolsError(error.message || 'Failed to fetch symbols');
        }
      } finally {
        if (!cancelled) {
          setLoadingAssetSymbols(false);
        }
      }
    };
    fetchSymbols();
    return () => {
      cancelled = true;
    };
  }, [API_BASE, universeConfig?.asset_class]);

  useEffect(() => {
    const assetClass = normalizeAssetClass(universeConfig?.asset_class);
    const watchlistActive = universeConfig.type === 'watchlist' && universeConfig.watchlist_id;
    if (assetClass === 'stocks' || watchlistActive) {
      return;
    }
    if (!assetSymbols.length || assetSymbolsAutoSelectedRef.current) {
      return;
    }
    const currentSymbols = normalizeSymbolList(universeConfig.symbols);
    if (currentSymbols.length === 0) {
      const allSymbols = normalizeSymbolList(assetSymbols.map(sym => sym.ticker));
      setUniverseConfig(prev => ({
        ...prev,
        asset_class: assetClass,
        symbols: allSymbols,
        type: prev.type || 'custom'
      }));
    }
    assetSymbolsAutoSelectedRef.current = true;
  }, [
    assetSymbols,
    universeConfig?.asset_class,
    universeConfig?.symbols,
    universeConfig?.type,
    universeConfig?.watchlist_id
  ]);

  // Check for backtest ID in React Router state and fetch from server
  useEffect(() => {
    const loadBacktestFromServer = async () => {
      console.log('ScreenBuilder: loadBacktestFromServer called');
      console.log('ScreenBuilder: location.state:', location.state);

      const searchParams = new URLSearchParams(location.search || '');
      const backtestId =
        searchParams.get('backtest') ||
        searchParams.get('backtest_id') ||
        searchParams.get('loadBacktestId') ||
        location.state?.loadBacktestId;
      const legacyBacktestData = location.state?.loadBacktest; // For backward compatibility

      if (backtestId) {
        // Check if we've already processed this backtest to prevent React StrictMode double execution
        if (processedBacktestRef.current === backtestId) {
          console.log('ScreenBuilder: Already processed this backtest, skipping...');
          return;
        }

        console.log('🔍 DEBUG END DATE: Processing backtest ID from server:', backtestId);
        console.log('🔍 DEBUG END DATE: Previously processed ID:', processedBacktestRef.current);

        // Mark this backtest as being processed IMMEDIATELY to prevent duplicates
        processedBacktestRef.current = backtestId;
        console.log('🔍 DEBUG END DATE: Marked backtest as processed');

        try {
          console.log('ScreenBuilder: Fetching backtest data from server for ID:', backtestId);

          // Fetch backtest data directly from the server
          const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4998';
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) throw new Error('No active session');

          const response = await fetch(`${API_BASE}/api/backtests/${backtestId}/load-screen`, {
            headers: { Authorization: `Bearer ${session.access_token}` }
          });

          if (!response.ok) throw new Error('Failed to load backtest settings from server');
          const data = await response.json();

          if (data.status !== 'success') {
            throw new Error(data.message || 'Failed to load backtest settings');
          }

          const backtestData = data.data;
          console.log('ScreenBuilder: Successfully fetched backtest data from server:', backtestData);

          // Now load the backtest data using the helper function
          await loadBacktestData(backtestData);

        } catch (error) {
          console.error('ScreenBuilder: Error fetching backtest from server:', error);
          alert('Error loading backtest settings from server: ' + error.message);
        }
      } else if (legacyBacktestData) {
        // Backward compatibility for old state-based approach
        console.log('ScreenBuilder: Using legacy backtest data from state');
        await loadBacktestData(legacyBacktestData);
      } else {
        console.log('ScreenBuilder: No backtest data in state, normal startup');
        // Reset the processed backtest ref when there's no data
        processedBacktestRef.current = null;
      }
    };

    // Helper function to load backtest data (extracted for reuse)
    const loadBacktestData = async (backtestData) => {
      console.log('🔍 DEBUG END DATE: Starting backtest data loading process');

      const strategyConfig = backtestData.strategy_config || {};
      const mergedStrategyConfig = {
        ...(backtestData.screen_config || {}),
        ...(strategyConfig || {})
      };

      console.log('ScreenBuilder: Loading backtest settings:', backtestData);
      console.log('ScreenBuilder: Strategy config:', strategyConfig);
      console.log('ScreenBuilder: Screen config:', backtestData.screen_config);
      console.log('ScreenBuilder: Backtest config:', backtestData.backtest_config);
      console.log('ScreenBuilder: Factors in strategy config:', strategyConfig?.factors);
      console.log('ScreenBuilder: Factors in screen config:', backtestData.screen_config?.factors);

      const elements = resolvePortfolioElements(mergedStrategyConfig);
      syncPortfolioCounter(elements);
      const primaryElement = elements[0] || createPortfolioElement({ asset_class: DEFAULT_ASSET_CLASS });
      setPortfolioElements(elements);
      setActiveElementId(primaryElement.id);
      setUniverseConfig(primaryElement.universe);
      setFormulas(primaryElement.formulas || []);
      setSelectionConfig(primaryElement.selection || cloneSelectionConfig(DEFAULT_SELECTION_V2));

      // Load factors - try multiple sources (legacy; formulas are now primary)
      let factorsToLoad = null;
      let factorsApplied = false;

      if (backtestData.screen_config?.factors) {
        console.log('ScreenBuilder: Loading factors from screen_config:', backtestData.screen_config.factors);
        factorsToLoad = backtestData.screen_config.factors;
      } else if (mergedStrategyConfig?.factors) {
        console.log('ScreenBuilder: Loading factors from strategy_config:', mergedStrategyConfig.factors);
        factorsToLoad = mergedStrategyConfig.factors;
      } else if (mergedStrategyConfig.selection?.factor) {
        // Fallback: enable the factor used in selection
        console.log('ScreenBuilder: Fallback - enabling factor from selection:', mergedStrategyConfig.selection.factor);
        const updatedFactors = MOCK_FACTORS.map(factor => ({
          ...factor,
          activated: factor.id === mergedStrategyConfig.selection.factor
        }));
        setFactors(updatedFactors);
        factorsApplied = true;
      }

      if (factorsToLoad && Array.isArray(factorsToLoad)) {
        console.log('ScreenBuilder: Setting factors to loaded factors:', factorsToLoad);
        setFactors(factorsToLoad);
        factorsApplied = true;
      }

      if (!factorsApplied && !factorsToLoad) {
        setFactors(MOCK_FACTORS);
      }

      if (backtestData.backtest_config_id) {
        setSelectedBacktestConfigId(backtestData.backtest_config_id);
      }
      if (backtestData.backtest_config?.optimizer) {
        const optimizerCfg = backtestData.backtest_config.optimizer;
        setOptimizerConfig((prev) => ({
          ...prev,
          start: optimizerCfg.start !== undefined ? String(optimizerCfg.start) : prev.start,
          end: optimizerCfg.end !== undefined ? String(optimizerCfg.end) : prev.end,
          step: optimizerCfg.step !== undefined ? String(optimizerCfg.step) : prev.step,
          target: optimizerCfg.target || prev.target,
          direction: optimizerCfg.direction || prev.direction
        }));
      }

      // Set the screen name
      setScreenName(backtestData.backtest_name || 'Loaded Backtest');

      console.log('ScreenBuilder: Loaded backtest settings into builder');

      // Set the backtest results if available
      if (backtestData.backtest_results) {
        // Transform the database backtest results into the format expected by EnhancedResultsPanel
        const dbResults = backtestData.backtest_results;
        const metrics = dbResults.metrics || {};
        const tradeMetrics = dbResults.trade_metrics || {};
        const rebalancesTable = dbResults.rebalances_table || dbResults.trades || dbResults.rebalances || [];
        const tradeFills = dbResults.trade_fills || dbResults.tradefills || [];
        const factorAnalysis = dbResults.factor_analysis || dbResults.factorAnalysis || [];
        const tradesRoundtrip = dbResults.trades_roundtrip || [];
        const topContributors = dbResults.top_contributors || [];
        const topDetractors = dbResults.top_detractors || [];
        const positionJumps = dbResults.position_jumps || [];
        const dataHandling = dbResults.data_handling || null;

        const totalTrades =
          (typeof tradeMetrics.total_trades === 'number')
            ? tradeMetrics.total_trades
            : (typeof tradeMetrics.trades === 'number')
              ? tradeMetrics.trades
              : tradeFills.length || tradesRoundtrip.length || 0;
        const winRate = (typeof tradeMetrics.win_rate === 'number')
          ? tradeMetrics.win_rate
          : (typeof metrics.win_rate === 'number' ? metrics.win_rate : 0);
        const avgTradeReturn = (typeof tradeMetrics.avg_trade_return === 'number')
          ? tradeMetrics.avg_trade_return
          : (typeof tradeMetrics.avg_return === 'number' ? tradeMetrics.avg_return : 0);
        const profitFactorTrades = (typeof tradeMetrics.profit_factor_trades === 'number')
          ? tradeMetrics.profit_factor_trades
          : (typeof tradeMetrics.profit_factor === 'number' ? tradeMetrics.profit_factor : 0);
        const avgHoldingDays = (typeof tradeMetrics.avg_holding_days === 'number')
          ? tradeMetrics.avg_holding_days
          : (typeof tradeMetrics.avg_holding === 'number' ? tradeMetrics.avg_holding : 0);
        const dailyRetVals = Array.isArray(dbResults.daily_returns)
          ? dbResults.daily_returns.map((d) => Number(d?.ret ?? d?.value) || 0)
          : [];
        const sortino = (typeof metrics.sortino === 'number')
          ? metrics.sortino
          : sortinoFromDaily(dailyRetVals);

        const transformedResults = {
          summary: {
            total_return: metrics.total_return || 0,
            annualized_return: metrics.cagr || 0,
            volatility: metrics.vol_annual || 0,
            sharpe_ratio: metrics.sharpe || 0,
            sortino_ratio: sortino,
            max_drawdown: Math.abs(metrics.max_drawdown || 0),
            win_rate: winRate,
            total_trades: totalTrades,
            avg_trade_return: avgTradeReturn,
            profit_factor: profitFactorTrades,
            calmar_ratio: metrics.calmar_ratio || null,
            avg_holding_days: avgHoldingDays
          },
          timeseries: dbResults.equity_curve?.map(pt => {
            const dd = dbResults.drawdown_curve?.find(d => d.date === pt.date);
            const ddMag = dd ? (typeof dd.drawdown_mag === 'number' ? dd.drawdown_mag : Math.abs(Number(dd.drawdown) || 0)) : 0;
            const bm = dbResults.benchmark_curve?.find(b => b.date === pt.date);
            return {
              date: pt.date,
              portfolio_value: Number(pt.equity),
              benchmark_value: bm ? Number(bm.equity) : undefined,
              drawdown: ddMag
            };
          }) || [],
          trades: rebalancesTable,
          rebalances_table: rebalancesTable,
          trade_fills: tradeFills,
          trades_roundtrip: tradesRoundtrip,
          explain: dbResults.explain || [],
          factor_analysis: factorAnalysis,
          top_contributors: topContributors,
          top_detractors: topDetractors,
          position_jumps: positionJumps,
          data_handling: dataHandling,
          warnings: dbResults.warnings || [],
          defaults_applied: dbResults.defaults_applied || [],
          params: dbResults.params || {}
        };
        if (dbResults.optimization) {
          transformedResults.optimization = dbResults.optimization;
          transformedResults.is_optimization = true;
        }

        console.log('ScreenBuilder: Set transformed backtest results in state');
        setResult(transformedResults);
        setShareTargets({
          screenResultId: null,
          backtestId: backtestData.backtest_id || backtestData.backtestId
        });
        setShareFeedback(null);

        console.log('ScreenBuilder: Setting showResults to true to display backtest results');
        setShowResults(true);

        console.log('ScreenBuilder: Backtest loading complete, results should be visible!');
      }
    };

    console.log('ScreenBuilder: useEffect running, calling loadBacktestFromServer');
    loadBacktestFromServer();
  }, [location.state, location.search]);

  const normalizedUniverseType = normalizeUniverseType(universeConfig.type);
  const normalizedAssetClass = normalizeAssetClass(universeConfig?.asset_class);
  const watchlistSelected = universeConfig.type === 'watchlist' && universeConfig.watchlist_id;
  const selectedSymbols = useMemo(
    () => normalizeSymbolList(universeConfig.symbols),
    [universeConfig.symbols]
  );
  const selectedSymbolSet = useMemo(
    () => new Set(selectedSymbols),
    [selectedSymbols]
  );
  const filteredAssetSymbols = useMemo(() => {
    const query = assetSymbolQuery.trim().toLowerCase();
    if (!query) {
      return assetSymbols;
    }
    return assetSymbols.filter((symbol) => {
      const ticker = String(symbol.ticker || '').toLowerCase();
      const name = String(symbol.company_name || symbol.name || '').toLowerCase();
      return ticker.includes(query) || name.includes(query);
    });
  }, [assetSymbolQuery, assetSymbols]);
  const hasManualSymbols = selectedSymbols.length > 0;
  const universeCompleted = normalizedAssetClass === 'stocks'
    ? (INDEX_UNIVERSE_TYPES.includes(normalizedUniverseType) || watchlistSelected || hasManualSymbols)
    : (watchlistSelected || hasManualSymbols);
  const filteredWatchlists = useMemo(() => (
    watchlists.filter(w => normalizeAssetClass(w.asset_class) === normalizedAssetClass)
  ), [watchlists, normalizedAssetClass]);
  const watchlistSelectedSet = useMemo(() => (
    new Set(watchlistDraftSymbols.map((symbol) => String(symbol.ticker || '').toUpperCase()).filter(Boolean))
  ), [watchlistDraftSymbols]);
  const watchlistFilteredSymbols = useMemo(() => {
    const query = watchlistSearchTerm.trim().toLowerCase();
    if (!query) {
      return watchlistSymbols;
    }
    return watchlistSymbols.filter((symbol) => {
      const ticker = String(symbol.ticker || '').toLowerCase();
      const name = String(symbol.company_name || symbol.name || '').toLowerCase();
      return ticker.includes(query) || name.includes(query);
    });
  }, [watchlistSearchTerm, watchlistSymbols]);

  const openWatchlistModal = useCallback(() => {
    setWatchlistDraftName('');
    setWatchlistDraftDescription('');
    setWatchlistDraftSymbols([]);
    setWatchlistBulkInput('');
    setWatchlistMissingSymbols([]);
    setWatchlistSearchTerm('');
    setWatchlistModalMessage(null);
    setShowWatchlistModal(true);
  }, []);

  const closeWatchlistModal = useCallback(() => {
    setShowWatchlistModal(false);
    setWatchlistModalMessage(null);
  }, []);

  const addWatchlistSymbols = useCallback((symbolsToAdd = []) => {
    const assetClass = normalizeAssetClass(universeConfig?.asset_class);
    setWatchlistDraftSymbols((prev) => {
      const next = new Map(
        prev.map((symbol) => [String(symbol.ticker || '').toUpperCase(), symbol])
      );
      symbolsToAdd.forEach((symbol) => {
        const ticker = normalizeSymbolList([symbol])[0];
        if (!ticker || next.has(ticker)) return;
        const entry = typeof symbol === 'object'
          ? {
              ticker,
              company_name: symbol.company_name || symbol.name || ticker,
              sector: symbol.sector || 'Unknown',
              asset_class: normalizeAssetClass(symbol.asset_class || assetClass)
            }
          : {
              ticker,
              company_name: ticker,
              sector: 'Unknown',
              asset_class: assetClass
            };
        next.set(ticker, entry);
      });
      return Array.from(next.values());
    });
  }, [universeConfig?.asset_class]);

  const handleWatchlistBulkAdd = useCallback(() => {
    const requested = parseSymbolTokens(watchlistBulkInput);
    if (requested.length === 0) {
      setWatchlistMissingSymbols([]);
      return;
    }
    const symbolMap = new Map(
      watchlistSymbols.map((symbol) => [String(symbol.ticker || '').toUpperCase(), symbol])
    );
    const missing = [];
    const toAdd = [];
    requested.forEach((ticker) => {
      const match = symbolMap.get(ticker);
      if (match) {
        toAdd.push(match);
      } else if (!missing.includes(ticker)) {
        missing.push(ticker);
      }
    });
    if (toAdd.length > 0) {
      addWatchlistSymbols(toAdd);
    }
    setWatchlistMissingSymbols(missing);
  }, [addWatchlistSymbols, watchlistBulkInput, watchlistSymbols]);

  const handleWatchlistAddSymbol = useCallback((symbol) => {
    if (!symbol) return;
    addWatchlistSymbols([symbol]);
  }, [addWatchlistSymbols]);

  const handleWatchlistRemoveSymbol = useCallback((ticker) => {
    const normalized = String(ticker || '').toUpperCase();
    if (!normalized) return;
    setWatchlistDraftSymbols((prev) => prev.filter(
      (symbol) => String(symbol.ticker || '').toUpperCase() !== normalized
    ));
  }, []);

  const handleUseUniverseSymbols = useCallback(() => {
    if (selectedSymbols.length === 0) return;
    const assetClass = normalizeAssetClass(universeConfig?.asset_class);
    const symbolMap = new Map(
      watchlistSymbols.map((symbol) => [String(symbol.ticker || '').toUpperCase(), symbol])
    );
    const toAdd = selectedSymbols.map((ticker) => (
      symbolMap.get(ticker) || {
        ticker,
        company_name: ticker,
        sector: 'Unknown',
        asset_class: assetClass
      }
    ));
    addWatchlistSymbols(toAdd);
  }, [addWatchlistSymbols, selectedSymbols, universeConfig?.asset_class, watchlistSymbols]);

  const handleCreateWatchlist = useCallback(async () => {
    const name = watchlistDraftName.trim();
    if (!name) {
      setWatchlistModalMessage({ text: 'Please enter a watchlist name.', isError: true });
      return;
    }
    try {
      setWatchlistSaving(true);
      setWatchlistModalMessage(null);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const symbolsPayload = watchlistDraftSymbols
        .map((symbol) => {
          const ticker = normalizeSymbolList([symbol])[0];
          if (!ticker) return null;
          return {
            ticker,
            company_name: symbol.company_name || symbol.name || ticker,
            sector: symbol.sector || 'Unknown',
            asset_class: normalizeAssetClass(symbol.asset_class || normalizedAssetClass)
          };
        })
        .filter(Boolean);

      const response = await fetch(`${API_BASE}/api/watchlists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          name,
          description: watchlistDraftDescription.trim(),
          asset_class: normalizedAssetClass,
          symbols: symbolsPayload
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create watchlist');
      }

      if (data?.watchlist_id) {
        setWatchlists((prev) => ([
          ...prev,
          {
            id: data.watchlist_id,
            name,
            description: watchlistDraftDescription.trim(),
            asset_class: normalizedAssetClass,
            symbols: symbolsPayload
          }
        ]));
        setUniverseConfig((prev) => ({
          ...prev,
          asset_class: normalizedAssetClass,
          type: 'watchlist',
          watchlist_id: data.watchlist_id,
          name
        }));
      }

      setShowWatchlistModal(false);
      setWatchlistDraftName('');
      setWatchlistDraftDescription('');
      setWatchlistDraftSymbols([]);
      setWatchlistBulkInput('');
      setWatchlistMissingSymbols([]);
      setWatchlistSearchTerm('');
      await fetchWatchlists();
    } catch (error) {
      setWatchlistModalMessage({
        text: error.message || 'Failed to create watchlist.',
        isError: true
      });
    } finally {
      setWatchlistSaving(false);
    }
  }, [
    API_BASE,
    fetchWatchlists,
    normalizedAssetClass,
    watchlistDraftDescription,
    watchlistDraftName,
    watchlistDraftSymbols
  ]);

  // Stepper configuration
  const steps = [
    {
      id: 'portfolio',
      title: 'Portfolio',
      description: 'Define portfolio elements',
      completed: portfolioElements.length > 0,
      active: activeStep === 'portfolio'
    },
    {
      id: 'universe',
      title: 'Universe',
      description: `Define ${getAssetClassLabel(universeConfig?.asset_class)} universe`,
      completed: universeCompleted,
      active: activeStep === 'universe'
    },
    {
      id: 'formula',
      title: 'Formula',
      description: 'Define screening formula',
      completed: formulas.some(f => f.formula && f.formula.trim().length > 0),
      active: activeStep === 'formula'
    },
    {
      id: 'selection',
      title: 'Selection',
      description: 'Choose selection criteria',
      completed: selectionConfig?.rankPick?.formula && selectionConfig.rankPick.formula.trim().length > 0,
      active: activeStep === 'selection'
    }
  ];

  const addFormula = () => {
    setFormulas(prev => {
      const newId = generateFormulaId(prev);
      return [
        ...prev,
        { id: newId, name: `Formula ${prev.length + 1}`, formula: '' }
      ];
    });
  };

  const removeFormula = (formulaId) => {
    setFormulas(prev => prev.filter(f => f.id !== formulaId));
  };

  const updateFormula = (formulaId, updates) => {
    setFormulas(prev => prev.map(f =>
      f.id === formulaId ? { ...f, ...updates } : f
    ));
  };

  const handleSelectPreset = (preset) => {
    // Add a new formula with the preset values
    setFormulas(prev => {
      const newId = generateFormulaId(prev);
      const newFormula = {
        id: newId,
        name: preset.name,
        formula: preset.formula
      };
      return [...prev, newFormula];
    });

    // Progress onboarding: after selecting a formula, blink Selection step to guide user
    if (onboardingStep <= 1) {
      setOnboardingStep(2);
    }
  };

  const generateDefaultScreenName = useCallback(() => {
    const now = new Date();
    const dateTime = now.toLocaleDateString() + ' ' + now.toLocaleTimeString();
    const activatedFactors = factors.filter(f => f.activated);
    const factorNames = activatedFactors.map(f => f.name).join(', ');

    if (factorNames) {
      return `${dateTime}, ${factorNames}`;
    } else {
      return `Strategy ${dateTime}`;
    }
  }, [factors]);

  const transformBacktestResponse = useCallback((server, runCfgOverride) => {
    return normalizeBacktestResult(
      server,
      runCfgOverride || selectedBacktestRunConfig || {}
    );
  }, [selectedBacktestRunConfig]);

  const buildStrategyConfig = useCallback(() => {
    const normalizedElements = (portfolioElements || []).map((element, index) => {
      const isActive = element.id === activeElementId;
      const elementUniverse = isActive ? universeConfig : element.universe;
      const elementFormulas = isActive ? formulas : element.formulas;
      const elementSelection = isActive ? selectionConfig : element.selection;
      const assetClass = normalizeAssetClass(element.asset_class || elementUniverse?.asset_class);
      const universe = resolveUniverseConfigFromConfig(elementUniverse || {}, assetClass);
      const weightFormulaRaw = element.weight_formula || '';
      const weightFormula = normalizeWeightFormulaInput(weightFormulaRaw).trim();
      const weightValue = evaluateWeightFormula(weightFormula);
      const resolvedWeight = Number.isFinite(weightValue)
        ? weightValue
        : sanitizeWeight(element.weight) || 0;
      return {
        id: element.id || `element-${index + 1}`,
        name: element.name || `Element ${index + 1}`,
        weight: resolvedWeight,
        weight_formula: weightFormula || undefined,
        asset_class: assetClass,
        universe,
        formulas: Array.isArray(elementFormulas) ? elementFormulas : [],
        selection: elementSelection || cloneSelectionConfig(DEFAULT_SELECTION_V2)
      };
    });

    const portfolio = { elements: normalizedElements };
    const active = normalizedElements.find(el => el.id === activeElementId) || normalizedElements[0];
    if (!active) {
      return {
        universe: universeConfig,
        formulas,
        selection: selectionConfig,
        portfolio
      };
    }
    return {
      universe: active.universe,
      formulas: active.formulas,
      selection: active.selection,
      portfolio
    };
  }, [activeElementId, formulas, portfolioElements, selectionConfig, universeConfig]);

  const buildRunnerStrategy = useCallback(() => {
    const strategy = buildStrategyConfig();
    if (strategy?.portfolio?.elements?.length) {
      return { portfolio: strategy.portfolio };
    }
    return strategy;
  }, [buildStrategyConfig]);

  const normalizeRunnerStrategy = useCallback((strategy) => {
    if (strategy?.portfolio?.elements?.length) {
      return { portfolio: strategy.portfolio };
    }
    return strategy;
  }, []);

  const validateScreenRunDate = useCallback(() => {
    if (screenRunTiming !== 'date') {
      return { isValid: true, date: null };
    }
    if (!screenRunDate) {
      return { isValid: false, errorMessage: 'Select a screen date.' };
    }
    if (screenRunDate > todayStr) {
      return {
        isValid: false,
        errorMessage: `Screen date cannot be in the future. Date: ${screenRunDate}, Today: ${todayStr}.`
      };
    }
    if (screenRunDate < earliestDateStr) {
      return {
        isValid: false,
        errorMessage: `${screenTierLabel} can only screen the last ${screenMaxYears} years. Earliest allowed date: ${earliestDateStr}. Requested date: ${screenRunDate}.`
      };
    }
    return { isValid: true, date: screenRunDate };
  }, [earliestDateStr, screenRunDate, screenRunTiming, screenTierLabel, screenMaxYears, todayStr]);

  const runStrategyDirect = useCallback(
    async (strategyPayload) => {
      if (!strategyPayload) return;

      const screenDateCheck = validateScreenRunDate();
      if (!screenDateCheck.isValid) {
        setRunError(screenDateCheck.errorMessage);
        return;
      }

      setIsRunning(true);
      setRunError(null);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('No active session');

        const response = await fetch(`${API_BASE}/api/run-screen`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            strategy: normalizeRunnerStrategy(strategyPayload),
            name: screenName || strategyPayload?.name,
            date: screenDateCheck.date
          })
        });

        if (!response.ok) throw new Error('Failed to run screen');

        const data = await response.json();
        setResult({
          screen_results: data,
          is_screen: true
        });
        setShareTargets({ screenResultId: data.screen_result_id || null, backtestId: null });
        setShareFeedback(null);
        setShowResults(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (err) {
        console.error('Error running screen:', err);
        setRunError(err.message || 'Failed to run screen');
      } finally {
        setIsRunning(false);
      }
    },
    [API_BASE, normalizeRunnerStrategy, screenName, validateScreenRunDate]
  );

  // Handle query params for loading a screen (and optional autorun)
  const loadScreenById = useCallback(
    async (screenId, options = {}, onLoaded) => {
      try {
        isLoadingScreenRef.current = true;
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('No active session');

        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4998';
        const resp = await fetch(`${API_BASE}/api/screens/${screenId}`, {
          headers: { Authorization: `Bearer ${session.access_token}` }
        });
        const js = await resp.json();
        if (!resp.ok) {
          throw new Error(js.message || 'Failed to load screen');
        }
        const screen = js.screen || js;
        const config = screen.strategy_config || {};

        hydrateBuilderFromStrategyConfig(config, {
          screenName: screen.name,
          currentScreen: screen,
          currentScreenId: screen.id
        });

        // Make sure results are hidden before optional autorun
        setShowResults(false);

        // If autorun requested, run immediately using the fetched config to avoid race conditions with state hydration
        if (options.autorun && config) {
          runStrategyDirect(config);
        }

        if (typeof onLoaded === 'function') {
          onLoaded(options);
        }
      } catch (err) {
        console.error('Error loading screen by id:', err);
      } finally {
        setTimeout(() => {
          isLoadingScreenRef.current = false;
        }, 100);
      }
    },
    [hydrateBuilderFromStrategyConfig, runStrategyDirect]
  );

  const loadScreenResultById = useCallback(
    async (resultId) => {
      try {
        isLoadingScreenRef.current = true;
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('No active session');

        const resp = await fetch(`${API_BASE}/api/screen-results/${resultId}`, {
          headers: { Authorization: `Bearer ${session.access_token}` }
        });
        const js = await resp.json();
        if (!resp.ok) {
          throw new Error(js.message || 'Failed to load screen results');
        }
        const record = js.screen_result || js;
        const strategyConfig = record.strategy_config || {};

        hydrateBuilderFromStrategyConfig(strategyConfig, {
          screenName: record.name || 'Screen Result',
          currentScreen: null,
          currentScreenId: record.screen_id || null
        });

        setResult({
          screen_results: record.screen_results,
          is_screen: true
        });
        setShareTargets({ screenResultId: record.id || resultId, backtestId: null });
        setShareFeedback(null);
        setShowResults(true);
      } catch (err) {
        console.error('Error loading screen results by id:', err);
      } finally {
        setTimeout(() => {
          isLoadingScreenRef.current = false;
        }, 100);
      }
    },
    [API_BASE, hydrateBuilderFromStrategyConfig]
  );

  const handleRunScreenOnUniverse = useCallback(async () => {
    const screenDateCheck = validateScreenRunDate();
    if (!screenDateCheck.isValid) {
      setRunError(screenDateCheck.errorMessage);
      return;
    }

    setIsRunning(true);
    setRunError(null);

    try {
      const strategy = buildRunnerStrategy();

      console.log('Running screen on current universe:', strategy);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const response = await fetch(`${API_BASE}/api/run-screen`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          strategy,
          name: screenName || strategy.name,
          date: screenDateCheck.date
        })
      });

      if (!response.ok) throw new Error('Failed to run screen');

      const data = await response.json();
      setResult({
        screen_results: data,
        is_screen: true
      });
      setShareTargets({ screenResultId: data.screen_result_id || null, backtestId: null });
      setShareFeedback(null);
      setShowResults(true);

      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (err) {
      console.error('Error running screen:', err);
      setRunError(err.message || 'Failed to run screen');
    } finally {
      setIsRunning(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE, buildRunnerStrategy, screenName, validateScreenRunDate]);

  // Handle query params for loading a local strategy draft
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const draftKey = params.get('draft') || params.get('draft_key') || params.get('strategy_key');
    if (!draftKey || processedQueryDraftRef.current === draftKey) {
      return;
    }
    if (!confirmNavigation()) {
      return;
    }
    processedQueryDraftRef.current = draftKey;

    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    const raw = window.localStorage.getItem(draftKey);
    if (!raw) {
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (err) {
      window.localStorage.removeItem(draftKey);
      return;
    }
    const strategyConfig = parsed?.strategy_config || parsed?.strategyConfig || parsed?.strategy || parsed?.config;
    if (!strategyConfig || typeof strategyConfig !== 'object') {
      window.localStorage.removeItem(draftKey);
      return;
    }
    hydrateBuilderFromStrategyConfig(strategyConfig, {
      screenName: parsed?.name || parsed?.title || 'Draft Strategy',
      currentScreen: null,
      currentScreenId: null
    });
  }, [confirmNavigation, hydrateBuilderFromStrategyConfig, location.search]);

  // Handle query params for loading a screen (and optional autorun)
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const screenId = params.get('load') || params.get('screen_id') || params.get('id');
    const autorun = params.get('run') === '1' || params.get('run') === 'true';
    if (screenId && !processedQueryScreenRef.current) {
      processedQueryScreenRef.current = true;
      loadScreenById(screenId, { autorun });
    }
  }, [loadScreenById, location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const resultId = params.get('screen_result') || params.get('screen_result_id');
    if (resultId && !processedQueryResultRef.current) {
      processedQueryResultRef.current = true;
      loadScreenResultById(resultId);
    }
  }, [loadScreenResultById, location.search]);

  const loadDevJsonIntoBuilder = useCallback(() => {
    if (!devModeEnabled) return;
    setDevPanelError(null);

    let payload;
    try {
      payload = devJsonPayload ? JSON.parse(devJsonPayload) : {};
    } catch (e) {
      setDevPanelError('Invalid JSON payload');
      return;
    }

    const strategyConfig = payload.strategy_config || payload.strategy || payload.strategyConfig;
    if (!strategyConfig) {
      setDevPanelError('Missing "strategy" or "strategy_config" in JSON');
      return;
    }

    if (!confirmNavigation()) {
      return;
    }

    hydrateBuilderFromStrategyConfig(strategyConfig, {
      screenName: payload.name || 'Dev JSON Strategy',
      currentScreen: null,
      currentScreenId: null
    });
    if (payload.backtest_config_id) {
      setSelectedBacktestConfigId(payload.backtest_config_id);
    }
  }, [confirmNavigation, devJsonPayload, devModeEnabled, hydrateBuilderFromStrategyConfig]);

  const runDevJson = useCallback(async (mode) => {
    if (!devModeEnabled) return;
    setDevPanelError(null);
    let payload;
    try {
      payload = devJsonPayload ? JSON.parse(devJsonPayload) : {};
    } catch (e) {
      setDevPanelError('Invalid JSON payload');
      return;
    }

    if (!payload.strategy) {
      setDevPanelError('Missing "strategy" in JSON');
      return;
    }
    if (mode === 'backtest' && !payload.run && !payload.backtest_config && !payload.backtest_config_id) {
      setDevPanelError('Missing "backtest_config" or "run" block for backtest');
      return;
    }

    setIsRunning(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      if (mode === 'screen') {
        const resp = await fetch(`${API_BASE}/api/run-screen`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ strategy: payload.strategy })
        });
        if (!resp.ok) throw new Error('Failed to run screen');
        const data = await resp.json();
        setResult({
          screen_results: data,
          is_screen: true
        });
        setShareTargets({ screenResultId: data.screen_result_id || null, backtestId: null });
        setShareFeedback(null);
        setShowResults(true);
      } else {
        const devRunConfig = payload.backtest_config?.backtest || payload.run;
        const resp = await fetch(`${API_BASE}/api/backtests`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            name: payload.name || `Backtest ${new Date().toLocaleString()}`,
            strategy: payload.strategy,
            backtest_config_id: payload.backtest_config_id,
            backtest_config: payload.backtest_config,
            run: payload.run,
            progress_token: payload.progress_token
          })
        });
        if (!resp.ok) {
          const errBody = await resp.json().catch(() => ({}));
          throw new Error(errBody.message || 'Failed to run backtest');
        }
        const body = await resp.json();
        const server = body.result || body;
        const normalized = transformBacktestResponse(server, devRunConfig);
        setResult(normalized);
        setShareTargets({ screenResultId: null, backtestId: body.backtest_id || server.backtest_id || null });
        setShareFeedback(null);
        setShowResults(true);
      }
    } catch (err) {
      console.error('Dev JSON run failed:', err);
      setDevPanelError(err.message || 'Run failed');
    } finally {
      setIsRunning(false);
    }
  }, [API_BASE, devJsonPayload, devModeEnabled, transformBacktestResponse]);

  const handleRunBacktest = async () => {
    const activeConfig = selectedBacktestConfig;
    const runConfig = activeConfig?.backtest || activeConfig?.run;

    if (!activeConfig) {
      setRunError('Select a backtest configuration before running.');
      return;
    }
    if (!runConfig?.start_date || !runConfig?.end_date) {
      setRunError('Backtest configuration is missing a start or end date.');
      return;
    }
    let optimizerPayload = null;
    if (hasOptimizerVar) {
      if (!optimizerAllowed) {
        setRunError('Optimizer is available on paid plans only.');
        return;
      }
      if (optimizerStepInfo.error) {
        setRunError(optimizerStepInfo.error);
        return;
      }
      const start = Number(optimizerConfig.start);
      const end = Number(optimizerConfig.end);
      const step = Number(optimizerConfig.step);
      if (!Number.isFinite(start) || !Number.isFinite(end) || !Number.isFinite(step)) {
        setRunError('Optimizer range requires numeric start, end, and step values.');
        return;
      }
      optimizerPayload = {
        start,
        end,
        step,
        target: optimizerConfig.target,
        direction: optimizerConfig.direction
      };
    }

    setIsRunning(true);
    setResult(undefined);
    setShareTargets({ screenResultId: null, backtestId: null });
    setShareFeedback(null);
    setDefaultsBanner(undefined);

    let progressToken = null;
    let backtestSucceeded = false;

    setBacktestProgress({
      status: 'running',
      percent: 0,
      message: 'Preparing backtest...',
      current_date: runConfig.start_date
    });

    try {
      progressToken = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
        ? crypto.randomUUID()
        : `bt-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      progressTokenRef.current = progressToken;

      setBacktestProgress((prev) => prev ? {
        ...prev,
        status: 'running',
        percent: 0,
        message: 'Preparing backtest...'
      } : {
        status: 'running',
        percent: 0,
        message: 'Preparing backtest...',
        current_date: runConfig.start_date
      });

      // Build strategy from current UI state (includes portfolio elements)
      const strategy = buildRunnerStrategy();

      // Validate date range against user's tier limits
      const startDate = new Date(runConfig.start_date);
      const endDate = new Date(runConfig.end_date);
      const validation = validateBacktestDateRange(startDate, endDate, billingTier || 'free');

      if (!validation.isValid) {
        setRunError(validation.errorMessage);
        setBacktestProgress((prev) => prev ? {
          ...prev,
          status: 'error',
          percent: 0,
          message: validation.errorMessage
        } : {
          status: 'error',
          percent: 0,
          message: validation.errorMessage
        });
        return;
      }

      startProgressPolling(progressToken);

      const payload = {
        strategy,
        backtest_config_id: selectedBacktestConfigId,
        name: screenName || `Backtest ${new Date().toLocaleString()}`
      };

      console.debug("POST /backtests payload:", payload);

      // Clear any previous errors
      setRunError(null);

      // POST to backend using proper API function
      let data = await postBacktest(strategy, activeConfig, {
        name: screenName || `Backtest ${new Date().toLocaleString()}`,
        progressToken,
        backtestConfigId: selectedBacktestConfigId,
        optimizer: optimizerPayload
      });

      const latestProgress = progressToken ? await pollProgressOnce(progressToken) : null;
      backtestSucceeded = !latestProgress || latestProgress.status !== 'error';

      // Process the response data
      const server = data;
      data = transformBacktestResponse(server, runConfig);

      setResult(data);
      setShareTargets({ screenResultId: null, backtestId: server?.backtest_id || null });
      setShareFeedback(null);
      setShowResults(true);

      window.scrollTo({ top: 0, behavior: 'smooth' });

      if (Array.isArray(data?.defaults_applied) && data.defaults_applied.length > 0) {
        setDefaultsBanner({
          applied: data.defaults_applied,
          onDismiss: () => setDefaultsBanner(undefined)
        });
      }

      if (backtestSucceeded) {
        loadBacktestConfigs();
      }
    } catch (err) {
      console.error("Backtest failed:", err);
      // Handle tier restriction errors from server
      if (err.message && err.message.includes('tier')) {
        setRunError(err.message);
      } else {
        setRunError(`Backtest failed: ${err.message || 'Unknown error'}`);
      }
      if (progressToken) {
        setBacktestProgress(prev => prev ? {
          ...prev,
          status: 'error',
          message: err.message || 'Backtest failed'
        } : {
          status: 'error',
          percent: 0,
          message: err.message || 'Backtest failed'
        });
      } else {
        setBacktestProgress({
          status: 'error',
          percent: 0,
          message: err.message || 'Backtest failed'
        });
        setTimeout(() => setBacktestProgress(null), 1500);
      }
    } finally {
      stopProgressPolling();
      progressTokenRef.current = null;
      if (progressToken) {
        const delay = backtestSucceeded ? 800 : 1500;
        setTimeout(() => setBacktestProgress(null), delay);
      }
      setIsRunning(false);
    }
  };

  // Auto-generate screen name when factors change (for new screens only)
  useEffect(() => {
    // Only auto-generate for new screens that don't have a saved ID yet
    if (!currentScreenId) {
      const activatedFactors = factors.filter(f => f.activated);
      // Only update if we have factors activated or the screen name is empty
      if (activatedFactors.length > 0 || !screenName.trim()) {
        const defaultName = generateDefaultScreenName();
        setScreenName(defaultName);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factors, currentScreenId]); // Remove screenName from dependencies to avoid infinite loop

  const canRun = steps.every(step => step.completed);

  return (
    <>
      <style>{`
        @keyframes onboarding-blink {
          0%, 100% {
            background-color: transparent;
            box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
          }
          50% {
            background-color: rgba(59, 130, 246, 0.1);
            box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2);
          }
        }
        .onboarding-blink {
          animation: onboarding-blink 1.5s ease-in-out infinite;
        }
      `}</style>
      {backtestProgress && <BacktestProgressOverlay progress={backtestProgress} />}
      <div className="linear-strategy-editor">
        {/* Main Header */}
        <div className="main-header mb-6">
          <h1 className="text-3xl font-bold mb-2">Strategy</h1>
          <p className="text-gray-600">Build and manage your stock screening strategies</p>
        </div>

        {/* Premium Signup Banner */}
        <PremiumSignupBanner isPremium={isPremium} />

        {/* Strategy Builder Header */}
        {!showResults && (
          <div className="screen-management-header mb-4">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-4">
                <h2 className="text-xl font-bold">Strategy Builder</h2>
                {currentScreen && (
                  <span className="text-lg text-gray-600">
                    Editing: {currentScreen.name}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Strategy name..."
                  value={screenName}
                  onChange={(e) => setScreenName(e.target.value)}
                  className="px-3 py-2 border rounded-md"
                />
              </div>
            </div>
          </div>
        )}

        {!showResults && (
          <div className="editor-header">
            <Stepper
              steps={steps}
              onStepClick={(stepId) => {
                setActiveStep(stepId);
                // When user clicks on Selection step during onboarding, progress to next step
                if (stepId === 'selection' && onboardingStep === 2) {
                  setOnboardingStep(3);
                }
              }}
              onboardingStep={onboardingStep}
            />
          </div>
        )}

        <div className={`editor-content ${showResults ? 'with-results' : ''}`}>
          <div className="main-panel">
                {/* Main Content Area - Shows only the active step or results */}
                <div className="main-content">
                  {devModeEnabled && (
                    <div className="mb-4 border rounded-lg border-dashed border-gray-300 bg-gray-50 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="text-sm font-semibold text-gray-800">Developer JSON Runner</div>
                          <div className="text-xs text-gray-600">Paste a full strategy/backtest payload to run or load into the builder.</div>
                        </div>
                        <label className="flex items-center gap-2 text-xs text-gray-700">
                          <input
                            type="checkbox"
                            checked={devModeEnabled}
                            onChange={(e) => setDevModeEnabled(e.target.checked)}
                          />
                          Enabled locally
                        </label>
                      </div>
                      <textarea
                        className="w-full h-32 border rounded-md p-2 text-xs font-mono"
                        value={devJsonPayload}
                        onChange={(e) => setDevJsonPayload(e.target.value)}
                        placeholder='{"strategy": {...}, "run": {...}}'
                      />
                      {devPanelError && (
                        <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                          {devPanelError}
                        </div>
                      )}
                      <div className="flex gap-2 mt-3">
                        <button
                          className="px-3 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
                          onClick={() => runDevJson('screen')}
                          disabled={isRunning}
                        >
                          Run Screen (JSON)
                        </button>
                        <button
                          className="px-3 py-2 rounded-md bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
                          onClick={() => runDevJson('backtest')}
                          disabled={isRunning}
                        >
                          Run Backtest (JSON)
                        </button>
                        <button
                          className="px-3 py-2 rounded-md bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-50"
                          onClick={loadDevJsonIntoBuilder}
                          disabled={isRunning}
                        >
                          Load Builder (JSON)
                        </button>
                      </div>
                    </div>
                  )}
                  {showResults && (
                    <div className="results-main-canvas">
                      <div className="results-header mb-4">
                        <div className="flex items-center justify-between">
                          <h2 className="text-2xl font-bold">
                            {result?.is_screen ? 'Screen Results' : (isOptimizationResult ? 'Optimization Results' : 'Backtest Results')}
                          </h2>
                          <div className="flex items-center gap-3">
                            {result?.is_screen && (
                              <button
                                onClick={handleRunBacktest}
                                disabled={isRunning}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isRunning ? 'Running...' : 'Run Backtest'}
                              </button>
                            )}
                            <button
                              onClick={handleShareResult}
                              disabled={!canShareResults || sharing}
                              className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                              <Share2 className="h-4 w-4" />
                              {sharing ? 'Sharing...' : 'Share Link'}
                            </button>
                            <button
                              onClick={() => setShowResults(false)}
                              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                            >
                              Back to Builder
                            </button>
                          </div>
                        </div>
                        {shareFeedback && (
                          <div className={`mt-2 text-sm ${shareFeedback.isError ? 'text-red-600' : 'text-emerald-600'}`}>
                            {shareFeedback.message}
                          </div>
                        )}
                      </div>
                      {result?.is_screen ? (
                        <PortfolioScreenResults
                          result={result?.screen_results || result}
                          screenName={screenName}
                          portfolioElements={portfolioElements}
                        />
                      ) : isOptimizationResult ? (
                        <OptimizationResultsPanel result={result} />
                      ) : (
                        <EnhancedResultsPanel result={result} isRunning={isRunning} />
                      )}
                    </div>
                  )}

                  {!showResults && activeStep === 'portfolio' && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Portfolio</CardTitle>
                      <p className="text-sm text-gray-600">Group one or more elements into a single strategy portfolio</p>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600">Each element has its own universe, formulas, and selection.</p>
                          </div>
                          <button
                            onClick={addPortfolioElement}
                            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                          >
                            Add Element
                          </button>
                        </div>
                        <div className="space-y-3">
                          {portfolioElements.map((element, index) => {
                            const isActive = element.id === activeElementId;
                            const universeLabel = element.universe?.name || (element.universe?.type === 'watchlist' ? 'Custom Watchlist' : element.universe?.type);
                            const weightFormula = element.weight_formula || '';
                            const weightPreview = evaluateWeightFormula(weightFormula);
                            const weightValidation = weightFormulaValidation[element.id];
                            const weightDebug = weightFormulaDebug[element.id];
                            const weightDebugLoading = weightFormulaDebugLoading[element.id];
                            return (
                              <div
                                key={element.id}
                                className={`rounded-lg border p-4 ${isActive ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'}`}
                              >
                                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                  <div className="flex-1 space-y-2">
                                    <div className="flex items-center gap-3">
                                      <input
                                        type="text"
                                        value={element.name || `Element ${index + 1}`}
                                        onChange={(e) => updatePortfolioElement(element.id, { name: e.target.value })}
                                        className="w-full max-w-xs rounded-md border border-gray-300 px-2 py-1 text-sm font-medium focus:border-blue-500 focus:outline-none"
                                      />
                                    </div>
                                    <div className="text-sm text-gray-600">
                                      Asset class: <span className="font-medium">{getAssetClassLabel(element.asset_class)}</span>
                                    </div>
                                    <div className="text-sm text-gray-600">
                                      Universe: <span className="font-medium">{universeLabel || 'Unconfigured'}</span>
                                    </div>
                                    <div className="space-y-2 text-sm text-gray-600">
                                      <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Weight Formula (%)
                                      </label>
                                      <input
                                        type="text"
                                        inputMode="decimal"
                                        value={weightFormula}
                                        onChange={(e) => {
                                          const nextRaw = normalizeWeightFormulaInput(e.target.value);
                                          const nextPreview = evaluateWeightFormula(nextRaw);
                                          updatePortfolioElement(element.id, {
                                            weight_formula: nextRaw,
                                            weight: Number.isFinite(nextPreview) ? nextPreview : element.weight
                                          });
                                          scheduleWeightFormulaValidation(element.id, nextRaw);
                                        }}
                                        placeholder="e.g., 50 or 2 + 98"
                                        className="w-48 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                                      />
                                      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                        <span>Presets:</span>
                                        {WEIGHT_FORMULA_PRESETS.map((preset) => (
                                          <button
                                            key={preset.value}
                                            type="button"
                                            onClick={() => {
                                              updatePortfolioElement(element.id, {
                                                weight_formula: preset.value,
                                                weight: Number(preset.value)
                                              });
                                              scheduleWeightFormulaValidation(element.id, preset.value);
                                            }}
                                            className="px-2 py-0.5 rounded border border-gray-300 hover:border-blue-400 hover:text-blue-600"
                                          >
                                            {preset.label}
                                          </button>
                                        ))}
                                      </div>
                                      {Number.isFinite(weightPreview) && (
                                        <div className="text-xs text-gray-500">
                                          Preview: {roundWeight(weightPreview)}%
                                        </div>
                                      )}
                                      {weightValidation?.valid === false && (
                                        <div className="text-xs text-red-600">
                                          Invalid formula: {weightValidation.error || 'Check syntax.'}
                                        </div>
                                      )}
                                      {weightValidation?.valid === true && weightFormula.trim() && (
                                        <div className="text-xs text-emerald-600">Formula looks valid.</div>
                                      )}
                                      {weightValidation?.valid === true && weightFormula.trim() && weightPreview === null && (
                                        <div className="text-xs text-amber-600">
                                          Local preview supports numeric expressions only. Use the API preview for data-driven formulas.
                                        </div>
                                      )}
                                      {weightValidation?.valid === true && weightFormula.trim() && (weightDebugLoading || weightDebug) && (
                                        <div className="text-xs text-gray-500">
                                          {weightDebugLoading ? (
                                            <div>Fetching latest API value...</div>
                                          ) : weightDebug?.error ? (
                                            <div className="text-red-600">API preview error: {weightDebug.error}</div>
                                          ) : (
                                            <>
                                              <div>
                                                API preview ({weightDebug?.symbol || 'AAPL'}): {formatDebugNumber(weightDebug?.value)}
                                              </div>
                                              <div className="text-[11px] text-gray-400">
                                                {weightDebug?.screen_date ? `As of ${weightDebug.screen_date}` : 'Latest available date'}
                                                {weightDebug?.message ? ` - ${weightDebug.message}` : ''}
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => selectPortfolioElement(element.id, { jumpToUniverse: true })}
                                      className="px-3 py-2 text-sm rounded-md border border-blue-500 text-blue-600 hover:bg-blue-50"
                                    >
                                      Configure
                                    </button>
                                    {portfolioElements.length > 1 && (
                                      <button
                                        onClick={() => removePortfolioElement(element.id)}
                                        className="px-3 py-2 text-sm rounded-md border border-red-500 text-red-600 hover:bg-red-50"
                                      >
                                        Remove
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="text-xs text-gray-500 flex flex-wrap items-center gap-2">
                          <span>
                            Total weight:{' '}
                            {totalPortfolioWeight === null
                              ? 'N/A'
                              : `${roundWeight(totalPortfolioWeight)}%`}
                          </span>
                          <span>Unused weight stays in cash. Backtests fail if total exceeds 100%.</span>
                          {totalPortfolioWeight !== null && totalPortfolioWeight > DEFAULT_WEIGHT_TOTAL + 0.01 && (
                            <span className="text-red-600">Total exceeds 100%.</span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {!showResults && activeStep === 'universe' && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Investment Universe</CardTitle>
                      <p className="text-sm text-gray-600">Define the investment universe for your strategy</p>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Asset Class</label>
                          <select
                            value={normalizeAssetClass(universeConfig?.asset_class)}
                            onChange={(e) => {
                              const nextAssetClass = normalizeAssetClass(e.target.value);
                              setUniverseConfig(() => {
                                return createUniverseDefaults(nextAssetClass);
                              });
                            }}
                            className="w-full p-2 border rounded-md"
                          >
                            {ASSET_CLASS_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Investment Universe</label>
                          <select
                            value={
                              normalizedAssetClass === 'stocks' && INDEX_UNIVERSE_TYPES.includes(normalizedUniverseType)
                                ? normalizedUniverseType
                                : (universeConfig.type === 'watchlist' && universeConfig.watchlist_id)
                                  ? `watchlist-${universeConfig.watchlist_id}`
                                  : ''
                            }
                            onChange={(e) => {
                              const selectedValue = e.target.value;
                              if (selectedValue === 'new-watchlist') {
                                openWatchlistModal();
                                return;
                              }
                              const normalizedValue = normalizeUniverseType(selectedValue);
                              if (normalizedAssetClass === 'stocks' && INDEX_UNIVERSE_TYPES.includes(normalizedValue)) {
                                setUniverseConfig(prev => ({
                                  ...prev,
                                  asset_class: normalizeAssetClass(prev.asset_class),
                                  type: normalizedValue,
                                  watchlist_id: null,
                                  name: INDEX_NAME_MAP[normalizedValue] || prev.name
                                }));
                              } else if (selectedValue.startsWith('watchlist-')) {
                                const watchlistId = selectedValue.replace('watchlist-', '');
                                const watchlist = filteredWatchlists.find(w => String(w.id) === watchlistId);
                                console.log('Selected watchlist:', watchlistId, 'Found:', watchlist);
                                setUniverseConfig(prev => ({
                                  ...prev,
                                  asset_class: normalizeAssetClass(prev.asset_class),
                                  type: 'watchlist',
                                  watchlist_id: watchlistId,
                                  name: watchlist ? watchlist.name : 'Custom Watchlist'
                                }));
                              } else if (!selectedValue) {
                                setUniverseConfig(prev => ({
                                  ...prev,
                                  asset_class: normalizeAssetClass(prev.asset_class),
                                  type: 'custom',
                                  watchlist_id: null,
                                  name: `Custom ${getAssetClassLabel(prev.asset_class)}`
                                }));
                              }
                            }}
                            className="w-full p-2 border rounded-md"
                            disabled={loadingWatchlists}
                          >
                            {normalizedAssetClass !== 'stocks' && (
                              <option value="">Custom symbol list</option>
                            )}
                            {normalizedAssetClass === 'stocks' && INDEX_UNIVERSE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                            <option value="new-watchlist">New watchlist...</option>
                            {filteredWatchlists.map((watchlist) => (
                              <option key={watchlist.id} value={`watchlist-${watchlist.id}`}>
                                {watchlist.name} ({watchlist.symbols?.length || 0} symbols)
                              </option>
                            ))}
                          </select>
                          {loadingWatchlists && (
                            <p className="text-sm text-gray-500 mt-1">Loading watchlists...</p>
                          )}
                          {!loadingWatchlists && filteredWatchlists.length === 0 && normalizedAssetClass !== 'stocks' && (
                            <p className="text-sm text-gray-500 mt-1">No watchlists for this asset class yet. Use the custom list below.</p>
                          )}
                        </div>
                        {normalizedAssetClass !== 'stocks' && !watchlistSelected && (
                          <div>
                            <label className="block text-sm font-medium mb-2">
                              Select {getAssetClassLabel(normalizedAssetClass)} Symbols
                            </label>
                            <div className="space-y-2">
                              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                                <input
                                  type="text"
                                  value={assetSymbolQuery}
                                  onChange={(e) => setAssetSymbolQuery(e.target.value)}
                                  className="w-full md:max-w-sm p-2 border rounded-md"
                                  placeholder={`Search ${getAssetClassLabel(normalizedAssetClass)}...`}
                                />
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => setUniverseSymbols(assetSymbols.map(sym => sym.ticker))}
                                    className="px-3 py-1 text-xs rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                                  >
                                    Select all
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setUniverseSymbols([])}
                                    className="px-3 py-1 text-xs rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                                  >
                                    Clear
                                  </button>
                                </div>
                              </div>
                              {loadingAssetSymbols ? (
                                <p className="text-sm text-gray-500">Loading symbols...</p>
                              ) : assetSymbolsError ? (
                                <p className="text-sm text-red-600">{assetSymbolsError}</p>
                              ) : (
                                <div className="max-h-56 overflow-y-auto rounded-md border border-gray-200 p-2 space-y-1">
                                  {filteredAssetSymbols.length === 0 && (
                                    <p className="text-sm text-gray-500">No symbols match your search.</p>
                                  )}
                                  {filteredAssetSymbols.map((symbol) => {
                                    const ticker = String(symbol.ticker || '').toUpperCase();
                                    const displayName = symbol.company_name || symbol.name || ticker;
                                    return (
                                      <label key={ticker} className="flex items-center gap-2 text-sm">
                                        <input
                                          type="checkbox"
                                          checked={selectedSymbolSet.has(ticker)}
                                          onChange={() => toggleUniverseSymbol(ticker)}
                                        />
                                        <span className="font-medium">{ticker}</span>
                                        <span className="text-gray-500 truncate">{displayName}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                              <p className="text-xs text-gray-500">
                                Selected {selectedSymbols.length} of {assetSymbols.length}
                              </p>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="point-in-time"
                            checked={universeConfig.point_in_time}
                            onChange={(e) => setUniverseConfig(prev => ({ ...prev, point_in_time: e.target.checked }))}
                          />
                          <label htmlFor="point-in-time" className="text-sm">Point-in-time data</label>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Fundamental Lag (days)</label>
                          <input
                            type="number"
                            value={universeConfig.fundamental_lag_days}
                            onChange={(e) => setUniverseConfig(prev => ({ ...prev, fundamental_lag_days: Number(e.target.value) }))}
                            className="w-full p-2 border rounded-md"
                            min="0"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {!showResults && activeStep === 'formula' && (
                  <div className="grid grid-cols-12 gap-4">
                    {/* Left Rail: Formula Library */}
                    <div className="col-span-4">
                      <FormulaLibrary
                        assetClass={normalizeAssetClass(universeConfig?.asset_class)}
                        onSelectPreset={handleSelectPreset}
                        onboardingStep={onboardingStep}
                        onOnboardingComplete={(step) => setOnboardingStep(step)}
                      />
                    </div>

                    {/* Right Panel: Formula Editors */}
                    <div className="col-span-8 space-y-4">
                      <Card>
                        <CardHeader>
                          <CardTitle>Screening Formulas</CardTitle>
                          <p className="text-sm text-gray-600">Define formulas to rank and select assets. Select a preset above or create your own.</p>
                        </CardHeader>
                        <CardContent className="space-y-6">
                          {formulas.length === 0 ? (
                            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                              <h3 className="text-lg font-medium text-gray-900 mb-2">No formulas yet</h3>
                              <p className="text-gray-500">Select a preset above or add a custom formula below</p>
                            </div>
                          ) : (
                            formulas.map((formulaObj, index) => (
                            <div key={formulaObj.id} className="space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <span className="text-sm font-semibold text-blue-600">{index + 1}</span>
                                  </div>
                                  <input
                                    type="text"
                                    value={formulaObj.name}
                                    onChange={(e) => updateFormula(formulaObj.id, { name: e.target.value })}
                                    className="font-medium text-lg border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none px-2 py-1"
                                    placeholder="Formula name"
                                  />
                                </div>
                                <button
                                  onClick={() => removeFormula(formulaObj.id)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded"
                                >
                                  <X className="h-5 w-5" />
                                </button>
                              </div>
                              <FormulaEditor
                                value={formulaObj.formula}
                                onChange={(newFormula) => updateFormula(formulaObj.id, { formula: newFormula })}
                                apiBaseUrl={API_BASE}
                              />
                            </div>
                            ))
                          )}

                          <div className="flex justify-center pt-2">
                            <button
                              onClick={addFormula}
                              className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                            >
                              <Plus className="h-5 w-5" />
                              Add Custom Formula
                            </button>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                )}

                {!showResults && activeStep === 'selection' && (
                  <SelectionEditorV2
                    config={selectionConfig}
                    onChange={setSelectionConfig}
                    formulas={formulas}
                    universeConfig={universeConfig}
                    apiBaseUrl={API_BASE}
                    onboardingStep={onboardingStep}
                    onOnboardingComplete={async () => {
                      setOnboardingStep(4);
                      // Update backend to disable onboarding
                      try {
                        const { data: { session } } = await supabase.auth.getSession();
                        if (session) {
                          await fetch(`${API_BASE}/api/user/settings`, {
                            method: 'PUT',
                            headers: {
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${session.access_token}`
                            },
                            body: JSON.stringify({ onboarding_enabled: false })
                          });
                        }
                      } catch (err) {
                        console.error('Error updating onboarding setting:', err);
                      }
                    }}
                  />
                )}

              </div>

              {/* Run Bar - Always visible when not showing results */}
              {!showResults && (
                <div className="run-section">
                  <div className="flex flex-col gap-3 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-center gap-4">
                    <input
                      type="text"
                      value={screenName}
                      onChange={(e) => setScreenName(e.target.value)}
                      className="flex-1 max-w-md px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter strategy name..."
                    />
                    <button
                      onClick={handleRunScreenOnUniverse}
                      disabled={!canRun || isRunning}
                      className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      {isRunning ? 'Running Screen...' : 'Run Screen'}
                    </button>
                    <button
                      onClick={handleRunBacktest}
                      disabled={isRunning || !canRun || !selectedBacktestConfigId}
                      className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {isRunning ? 'Running Backtest...' : 'Run Backtest'}
                    </button>
                    {defaultsBanner && (
                      <div className="text-sm text-amber-600">
                        Some defaults were applied. Check your configuration.
                      </div>
                    )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Screen Run Date</div>
                      <div className="flex flex-wrap gap-2">
                        <label
                          className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition ${
                            screenRunTiming === 'now'
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-300 text-gray-700 hover:border-blue-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="screen-run-date"
                            checked={screenRunTiming === 'now'}
                            onChange={() => setScreenRunTiming('now')}
                          />
                          <span className="font-medium">Now</span>
                          <span className="text-xs text-gray-500">Latest data</span>
                        </label>
                        <label
                          className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition ${
                            screenRunTiming === 'date'
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-300 text-gray-700 hover:border-blue-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="screen-run-date"
                            checked={screenRunTiming === 'date'}
                            onChange={() => setScreenRunTiming('date')}
                          />
                          <span className="font-medium">As of</span>
                          <input
                            type="date"
                            value={screenRunDate}
                            min={earliestDateStr}
                            max={todayStr}
                            disabled={screenRunTiming !== 'date'}
                            onChange={(e) => {
                              setScreenRunDate(e.target.value);
                              setScreenRunTiming('date');
                            }}
                            className="ml-1 rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 disabled:opacity-60"
                          />
                        </label>
                      </div>
                      <div className="text-xs text-gray-600">
                        Your {screenTierLabel} plan allows screens up to {screenMaxYears} years back. Earliest allowed date: {earliestDateStr}.
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Backtest Configuration</div>
                      {loadingBacktestConfigs && (
                        <div className="text-sm text-gray-500">Loading backtest configurations...</div>
                      )}
                      {backtestConfigsError && (
                        <div className="text-sm text-red-600">{backtestConfigsError}</div>
                      )}
                      {!loadingBacktestConfigs && backtestConfigs.length === 0 && (
                        <div className="text-sm text-gray-500">No backtest configurations yet.</div>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {backtestConfigs.map((cfg) => (
                          <label
                            key={cfg.id}
                            className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition ${
                              selectedBacktestConfigId === cfg.id
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-gray-300 text-gray-700 hover:border-blue-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedBacktestConfigId === cfg.id}
                              onChange={() => setSelectedBacktestConfigId(cfg.id)}
                            />
                            <span className="font-medium">{cfg.name}</span>
                            {cfg.is_plan_default && (
                              <span className="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] uppercase tracking-wide text-amber-700">
                                Plan
                              </span>
                            )}
                            {cfg.is_default && (
                              <span className="ml-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] uppercase tracking-wide text-gray-600">
                                Default
                              </span>
                            )}
                          </label>
                        ))}
                      </div>
                      <div className="text-xs text-gray-600">
                        Runs are saved to History for reuse.
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Optimizer</div>
                      {!hasOptimizerVar && (
                        <div className="text-sm text-gray-600">
                          Add <span className="font-semibold">x</span> (case-insensitive, underscores ok like <span className="font-semibold">_x_</span>) to any formula or weight formula to enable optimization.
                        </div>
                      )}
                      {hasOptimizerVar && !optimizerAllowed && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                          Optimizer is available on paid plans only. Upgrade to unlock backtest optimization.
                        </div>
                      )}
                      {hasOptimizerVar && optimizerAllowed && (
                        <>
                          <div className="text-sm text-gray-600">
                            Optimizing variable <span className="font-semibold">x</span> across {optimizerStepInfo.count || 0} steps.
                          </div>
                          <div className="grid gap-3 md:grid-cols-2">
                            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
                              Range start
                              <input
                                type="number"
                                inputMode="decimal"
                                step="any"
                                value={optimizerConfig.start}
                                onChange={(e) => setOptimizerConfig((prev) => ({ ...prev, start: e.target.value }))}
                                className="rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
                              />
                            </label>
                            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
                              Range end
                              <input
                                type="number"
                                inputMode="decimal"
                                step="any"
                                value={optimizerConfig.end}
                                onChange={(e) => setOptimizerConfig((prev) => ({ ...prev, end: e.target.value }))}
                                className="rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
                              />
                            </label>
                            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
                              Step
                              <input
                                type="number"
                                inputMode="decimal"
                                step="any"
                                value={optimizerConfig.step}
                                onChange={(e) => setOptimizerConfig((prev) => ({ ...prev, step: e.target.value }))}
                                className="rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
                              />
                            </label>
                            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
                              Target
                              <select
                                value={optimizerConfig.target}
                                onChange={(e) => setOptimizerConfig((prev) => ({ ...prev, target: e.target.value }))}
                                className="rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
                              >
                                {OPTIMIZER_TARGET_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            </label>
                            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
                              Direction
                              <select
                                value={optimizerConfig.direction}
                                onChange={(e) => setOptimizerConfig((prev) => ({ ...prev, direction: e.target.value }))}
                                className="rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
                              >
                                <option value="max">Maximize</option>
                                <option value="min">Minimize</option>
                              </select>
                            </label>
                          </div>
                          <div className={`text-xs ${optimizerStepInfo.error ? 'text-red-600' : 'text-gray-600'}`}>
                            {optimizerStepInfo.error
                              ? optimizerStepInfo.error
                              : `Steps: ${optimizerStepInfo.count} (max ${OPTIMIZER_MAX_STEPS})`}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  {runError && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="text-red-600 font-semibold">✕</div>
                        <div className="text-sm text-red-800">{runError}</div>
                        <button
                          onClick={() => setRunError(null)}
                          className="ml-auto text-red-600 hover:text-red-700"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
      {showWatchlistModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-4xl overflow-hidden rounded-lg bg-white shadow-xl">
            <div className="flex items-start justify-between border-b px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Create Watchlist</h3>
                <p className="text-sm text-gray-600">
                  Save a watchlist for {getAssetClassLabel(normalizedAssetClass)} assets.
                </p>
              </div>
              <button
                type="button"
                onClick={closeWatchlistModal}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close watchlist modal"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-[80vh] overflow-y-auto px-6 py-4 space-y-6">
              {watchlistModalMessage && (
                <div
                  className={`rounded-md border px-4 py-3 text-sm ${
                    watchlistModalMessage.isError
                      ? 'border-red-200 bg-red-50 text-red-700'
                      : 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  }`}
                >
                  {watchlistModalMessage.text}
                </div>
              )}
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <input
                    type="text"
                    value={watchlistDraftName}
                    onChange={(e) => setWatchlistDraftName(e.target.value)}
                    className="w-full rounded-md border px-3 py-2"
                    placeholder="Watchlist name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <input
                    type="text"
                    value={watchlistDraftDescription}
                    onChange={(e) => setWatchlistDraftDescription(e.target.value)}
                    className="w-full rounded-md border px-3 py-2"
                    placeholder="Optional description"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <label className="block text-sm font-medium">Symbols</label>
                  {selectedSymbols.length > 0 && (
                    <button
                      type="button"
                      onClick={handleUseUniverseSymbols}
                      className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                    >
                      Use current selection ({selectedSymbols.length})
                    </button>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Paste symbols</label>
                  <div className="flex flex-col gap-2 md:flex-row md:items-start">
                    <textarea
                      rows={3}
                      value={watchlistBulkInput}
                      onChange={(e) => {
                        setWatchlistBulkInput(e.target.value);
                        if (watchlistMissingSymbols.length > 0) {
                          setWatchlistMissingSymbols([]);
                        }
                      }}
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                      placeholder="AAPL, MSFT, NVDA"
                    />
                    <button
                      type="button"
                      onClick={handleWatchlistBulkAdd}
                      className="px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      Add symbols
                    </button>
                  </div>
                  {watchlistMissingSymbols.length > 0 && (
                    <div className="mt-2 text-xs text-red-600">
                      Missing symbols: {watchlistMissingSymbols.join(', ')}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Search symbols</label>
                  <input
                    type="text"
                    value={watchlistSearchTerm}
                    onChange={(e) => setWatchlistSearchTerm(e.target.value)}
                    className="w-full rounded-md border px-3 py-2 text-sm"
                    placeholder={`Search ${getAssetClassLabel(normalizedAssetClass)} symbols...`}
                  />
                </div>
                <div className="max-h-48 overflow-y-auto rounded-md border border-gray-200">
                  {watchlistSymbolsLoading ? (
                    <div className="p-3 text-sm text-gray-500">Loading symbols...</div>
                  ) : watchlistSymbolsError ? (
                    <div className="p-3 text-sm text-red-600">{watchlistSymbolsError}</div>
                  ) : watchlistFilteredSymbols.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500">No symbols match your search.</div>
                  ) : (
                    watchlistFilteredSymbols.slice(0, 50).map((symbol) => {
                      const ticker = String(symbol.ticker || '');
                      const disabled = watchlistSelectedSet.has(ticker.toUpperCase());
                      return (
                        <div
                          key={ticker}
                          className="flex items-center justify-between gap-3 border-b px-3 py-2 last:border-b-0"
                        >
                          <div>
                            <div className="text-sm font-medium text-gray-900">{ticker}</div>
                            {(symbol.company_name || symbol.name) && (
                              <div className="text-xs text-gray-500">
                                {symbol.company_name || symbol.name}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleWatchlistAddSymbol(symbol)}
                            disabled={disabled}
                            className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Add
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between text-sm font-medium">
                  <span>Selected symbols</span>
                  <span className="text-xs text-gray-500">{watchlistDraftSymbols.length} selected</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {watchlistDraftSymbols.length === 0 ? (
                    <span className="text-sm text-gray-500">No symbols added yet.</span>
                  ) : (
                    watchlistDraftSymbols.map((symbol) => (
                      <span
                        key={symbol.ticker}
                        className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700"
                      >
                        {symbol.ticker}
                        <button
                          type="button"
                          onClick={() => handleWatchlistRemoveSymbol(symbol.ticker)}
                          className="text-gray-400 hover:text-gray-600"
                          aria-label={`Remove ${symbol.ticker}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeWatchlistModal}
                  className="px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateWatchlist}
                  disabled={watchlistSaving || !watchlistDraftName.trim()}
                  className="px-4 py-2 rounded-md bg-blue-600 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {watchlistSaving ? 'Creating...' : 'Create Watchlist'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default ScreenBuilder;
